// Dimension View - Handles 1D, 2D, and 3D visualizations for SINGLE CELL
// For multi-cell comparison, use DimensionViewMultiCell.jsx
import * as echarts from 'echarts'
import 'echarts-gl'
import { useEffect, useRef } from 'preact/hooks'
import { basis, domain, impedanceMode } from '../../../state/globalSignals.js'
import { formatValue } from '../../../utils/formatUtils.js'


export default function DimensionView({
  category,
  dimension,
  cellData,           // Single cell mode: cellData object
  domainValue,
  onToggleDimension
}) {
  const plot2dRef1 = useRef(null)
  const plot2dRef2 = useRef(null)
  const plot3dRef = useRef(null)
  const chartInstance1 = useRef(null)
  const chartInstance2 = useRef(null)
  const chart3DInstance = useRef(null)

  // State for AC impedance plot type: 'nyquist' or 'bode'
  // Map dimension to plot type: 2d=Nyquist, 3d=Bode
  const acPlotType = dimension === '3d' ? 'bode' : 'nyquist'

  // Get data based on category (use signal value if prop not provided)
  const activeDomain = domainValue || domain.value
  const activeImpedanceMode = impedanceMode.value

  // Get data for single cell
  const data = getDataForCategory(category, cellData, basis.value, activeDomain, activeImpedanceMode)

  // Debug logging
  useEffect(() => {
    console.log('=== DimensionView (Single Cell) Debug ===')
    console.log('Category:', category)
    console.log('Dimension:', dimension)
    console.log('Impedance Mode:', activeImpedanceMode)
    console.log('Data:', data)
    console.log('X length:', data?.X?.length)
    console.log('Y length:', data?.Y?.length)
    console.log('Z length:', data?.Z?.length)
    console.log('X0:', data?.X0)
    console.log('Y0:', data?.Y0)
    console.log('Z0:', data?.Z0)
    if (category === 'power') {
      console.log('P_peak:', data?.P_peak)
      console.log('P_cont:', data?.P_cont)
    }
  }, [category, dimension, data, activeImpedanceMode])

  useEffect(() => {
    if (dimension === '1d') return // No charts for 1D

    requestAnimationFrame(() => {
      // AC Impedance: always use 2D plots (Nyquist or Bode), even in "3D" mode
      if (category === 'ac-impedance') {
        render2DPlots(data, category)
      } else if (dimension === '2d') {
        render2DPlots(data, category)
      } else if (dimension === '3d') {
        render3DPlot(data, category)
      }
    })

    return () => {
      if (chartInstance1.current) chartInstance1.current.dispose()
      if (chartInstance2.current) chartInstance2.current.dispose()
      if (chart3DInstance.current) chart3DInstance.current.dispose()
    }
  }, [dimension, category, basis.value, domain.value, activeImpedanceMode, acPlotType])

  // Resize handling
  useEffect(() => {
    const handleResize = () => {
      if (chartInstance1.current) chartInstance1.current.resize()
      if (chartInstance2.current) chartInstance2.current.resize()
      if (chart3DInstance.current) chart3DInstance.current.resize()
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  function render2DPlots(data, categoryName) {
    if (!plot2dRef1.current || !plot2dRef2.current) return

    console.log('🔍 === RENDER 2D PLOTS - START ===')
    console.log('Category:', categoryName)
    console.log('Plot refs valid:', !!plot2dRef1.current, '&&', !!plot2dRef2.current)

    // Check if data exists and has required arrays
    if (!data || !data.X || !data.Y || !data.Z) {
      console.error('❌ MISSING DATA:', {
        hasData: !!data,
        hasX: !!data?.X,
        hasY: !!data?.Y,
        hasZ: !!data?.Z,
        xLength: data?.X?.length || 0,
        yLength: data?.Y?.length || 0,
        zLength: data?.Z?.length || 0
      })
      return
    }

    // Log raw array data sample
    console.log('📊 RAW ARRAY DATA:')
    console.log('  X array length:', data.X.length, '| First 10 values:', data.X.slice(0, 10).map(v => v.toFixed(2)))
    console.log('  Y array length:', data.Y.length, '| First 10 values:', data.Y.slice(0, 10).map(v => v.toFixed(2)))
    console.log('  Z array length:', data.Z.length, '| First 10 values:', data.Z.slice(0, 10).map(v => v.toFixed(2)))
    console.log('  Ranges - X:', Math.min(...data.X).toFixed(2), 'to', Math.max(...data.X).toFixed(2))
    console.log('  Ranges - Y:', Math.min(...data.Y).toFixed(2), 'to', Math.max(...data.Y).toFixed(2))
    console.log('  Ranges - Z:', Math.min(...data.Z).toFixed(2), 'to', Math.max(...data.Z).toFixed(2))

    // Special case for Voltage: show V vs Q curve (single plot)
    if (categoryName === 'voltage') {
      if (chartInstance1.current) chartInstance1.current.dispose()
      chartInstance1.current = echarts.init(plot2dRef1.current)

      // Create V vs Q data (X=Q, Y=V)
      const vqData = []
      for (let i = 0; i < data.X.length; i++) {
        vqData.push([data.X[i], data.Y[i]])
      }
      vqData.sort((a, b) => a[0] - b[0])

      // Calculate dynamic Y-axis range with some padding
      // IMPORTANT: Include V_min and V_max reference lines in the range calculation
      const voltages = data.Y
      const dataMinV = Math.min(...voltages)
      const dataMaxV = Math.max(...voltages)

      // Include V_min and V_max in the axis range so reference lines are visible
      const minV = Math.min(dataMinV, data.V_min || dataMinV)
      const maxV = Math.max(dataMaxV, data.V_max || dataMaxV)

      const vRange = maxV - minV
      const yMin = Math.max(0, minV - vRange * 0.1) // 10% padding below
      const yMax = maxV + vRange * 0.1 // 10% padding above

      chartInstance1.current.setOption({
        backgroundColor: 'transparent',
        grid: { left: 70, right: 80, top: 20, bottom: 70 },
        xAxis: {
          type: 'value',
          name: `${data.xLabel} / ${data.xUnit}`,
          nameLocation: 'middle',
          nameGap: 40,
          nameTextStyle: { color: '#cfd2d6', fontSize: 12, fontFamily: 'Roboto' },
          axisLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.15)' } },
          axisLabel: {
            show: true,
            color: '#999',
            fontFamily: 'Roboto',
            fontSize: 11,
            showMinLabel: false,
            showMaxLabel: false
          },
          splitLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.05)' } }
        },
        yAxis: {
          type: 'value',
          name: `${data.yLabel} / ${data.yUnit}`,
          nameLocation: 'middle',
          nameGap: 50,
          min: yMin,
          max: yMax,
          axisLabel: {
            show: true,
            color: '#999',
            fontFamily: 'Roboto',
            fontSize: 11,
            showMinLabel: false,
            showMaxLabel: false
          },
          nameTextStyle: { color: '#cfd2d6', fontSize: 12, fontFamily: 'Roboto' },
          axisLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.15)' } },
          splitLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.05)' } }
        },
        series: [
          {
            type: 'line',
            name: 'Discharge Curve',
            data: vqData,
            smooth: false,
            lineStyle: { color: '#ea5b0c', width: 3 },
            symbolSize: 0,
            z: 10
          },
          // V_max reference line (horizontal)
          {
            type: 'line',
            markLine: {
              silent: true,
              symbol: 'none',
              label: {
                show: true,
                position: 'insideEndTop',
                formatter: 'V_max',
                color: '#999',
                fontSize: 11,
                fontFamily: 'Roboto',
                distance: 5
              },
              lineStyle: {
                type: 'dashed',
                color: '#999',
                width: 1.5
              },
              data: [{ yAxis: data.V_max }]
            }
          },
          // V_mean reference line (horizontal)
          {
            type: 'line',
            markLine: {
              silent: true,
              symbol: 'none',
              label: {
                show: true,
                position: 'insideEndTop',
                formatter: 'V_mean',
                color: '#ea5b0c',
                fontSize: 11,
                fontFamily: 'Roboto',
                distance: 5
              },
              lineStyle: {
                type: 'dashed',
                color: '#ea5b0c',
                width: 1.5
              },
              data: [{ yAxis: data.V_mean }]
            }
          },
          // V_min reference line (horizontal)
          {
            type: 'line',
            markLine: {
              silent: true,
              symbol: 'none',
              label: {
                show: true,
                position: 'insideEndBottom',
                formatter: 'V_min',
                color: '#999',
                fontSize: 11,
                fontFamily: 'Roboto',
                distance: 5
              },
              lineStyle: {
                type: 'dashed',
                color: '#999',
                width: 1.5
              },
              data: [{ yAxis: data.V_min }]
            }
          }
        ],
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(45, 42, 38, 0.95)',
          borderColor: '#ea5b0c',
          borderWidth: 1,
          textStyle: { color: '#fff', fontFamily: 'Roboto', fontSize: 12 },
          formatter: (params) => {
            if (!params || params.length === 0) return ''
            const param = params[0]

            const capacity = param.value[0]
            const voltage = param.value[1]

            const capacityRounded = Math.abs(capacity) >= 100 ? capacity.toFixed(1) :
                                    Math.abs(capacity) >= 10 ? capacity.toFixed(2) :
                                    capacity.toFixed(3)
            const voltageRounded = formatValue(voltage, 'V')

            return `${data.xLabel}: ${capacityRounded} ${data.xUnit}<br/>${data.yLabel}: ${voltageRounded} ${data.yUnit}`
          }
        }
      })

      // Hide second plot for voltage
      if (chartInstance2.current) chartInstance2.current.dispose()
      if (plot2dRef2.current) plot2dRef2.current.style.display = 'none'

      return
    }

    // Special case for AC Impedance: show Nyquist or Bode plots (full width like Voltage)
    if (categoryName === 'ac-impedance') {
      if (acPlotType === 'nyquist') {
        // === NYQUIST PLOT: -Z" vs Z' (single plot, full width) ===
        if (chartInstance1.current) chartInstance1.current.dispose()
        chartInstance1.current = echarts.init(plot2dRef1.current)

        // Create Nyquist data (X=Z_real, Y=-Z_imag)
        const nyquistData = []
        for (let i = 0; i < data.X.length; i++) {
          nyquistData.push([data.X[i], data.Y[i]])
        }

        // Find Y-value on Nyquist curve at R_pol position (X = R_ac + R_pol)
        const rPolX = data.R_ac_25 + (data.R_pol_25 || 0)
        let rPolY = 0
        if (data.X && data.Y && data.X.length > 0) {
          // Find closest X value to rPolX
          let closestIndex = 0
          let minDiff = Math.abs(data.X[0] - rPolX)
          for (let i = 1; i < data.X.length; i++) {
            const diff = Math.abs(data.X[i] - rPolX)
            if (diff < minDiff) {
              minDiff = diff
              closestIndex = i
            }
          }
          rPolY = data.Y[closestIndex]
        }

        // Calculate dynamic X-axis and Y-axis limits for Nyquist with EQUAL ASPECT RATIO
        const realValues = data.X || []
        const imagValues = data.Y || []

        console.log('Nyquist plot data points:', nyquistData.length)
        console.log('Sample points:', nyquistData.slice(0, 5))
        console.log('R_ac_25:', data.R_ac_25, 'R_EIS_25:', data.R_EIS_25)

        // Filter out invalid values
        const validRealValues = realValues.filter(v => !isNaN(v) && isFinite(v))
        const validImagValues = imagValues.filter(v => !isNaN(v) && isFinite(v))

        // Get min/max for each axis
        const xMin = validRealValues.length > 0 ? Math.min(...validRealValues) : 0
        const xMax = validRealValues.length > 0 ? Math.max(...validRealValues) : 1
        const yMin = validImagValues.length > 0 ? Math.min(...validImagValues) : 0
        const yMax = validImagValues.length > 0 ? Math.max(...validImagValues) : 1

        // Calculate ranges
        const xRange = xMax - xMin
        const yRange = yMax - yMin

        // Use maximum range for BOTH axes to ensure square grid (1:1 aspect ratio)
        const maxRange = Math.max(xRange, yRange)

        // Center both axes around their data and use the same range
        const xCenter = (xMax + xMin) / 2
        const yCenter = (yMax + yMin) / 2

        // Apply 10% padding to the max range
        const paddedRange = maxRange * 1.1

        // Calculate equal limits for both axes (start from 0 if all values positive)
        const xAxisMin = xMin > 0 ? 0 : xCenter - paddedRange / 2
        const xAxisMax = xCenter + paddedRange / 2
        const yAxisMin = yMin > 0 ? 0 : yCenter - paddedRange / 2
        const yAxisMax = yCenter + paddedRange / 2

        // Calculate grid dimensions for equal pixel scaling
        const containerWidth = plot2dRef1.current.offsetWidth
        const containerHeight = plot2dRef1.current.offsetHeight

        // Grid margins
        const gridLeft = 70
        const gridRight = 30
        const gridTop = 50
        const gridBottom = 70

        const gridWidth = containerWidth - gridLeft - gridRight
        const gridHeight = containerHeight - gridTop - gridBottom

        // Calculate pixels per unit - use the more restrictive axis
        const xAxisRange = xAxisMax - xAxisMin
        const yAxisRange = yAxisMax - yAxisMin

        const pixelsPerUnitX = gridWidth / xAxisRange
        const pixelsPerUnitY = gridHeight / yAxisRange
        const pixelsPerUnit = Math.min(pixelsPerUnitX, pixelsPerUnitY)

        // Adjust axis ranges to fill grid while maintaining equal scaling
        const dataCenterX = (xAxisMin + xAxisMax) / 2
        const dataCenterY = (yAxisMin + yAxisMax) / 2

        const adjustedXRange = gridWidth / pixelsPerUnit
        const adjustedYRange = gridHeight / pixelsPerUnit

        const xAxisMinAdjusted = dataCenterX - adjustedXRange / 2
        const xAxisMaxAdjusted = dataCenterX + adjustedXRange / 2
        const yAxisMinAdjusted = dataCenterY - adjustedYRange / 2
        const yAxisMaxAdjusted = dataCenterY + adjustedYRange / 2

        // Calculate nice tick interval
        const targetPixelsPerTick = 100
        const dataUnitsPerTick = targetPixelsPerTick / pixelsPerUnit

        const magnitude = Math.pow(10, Math.floor(Math.log10(dataUnitsPerTick)))
        const normalized = dataUnitsPerTick / magnitude
        let niceInterval
        if (normalized < 1.5) niceInterval = 1 * magnitude
        else if (normalized < 3) niceInterval = 2 * magnitude
        else if (normalized < 7) niceInterval = 5 * magnitude
        else niceInterval = 10 * magnitude

        // Round axis limits to nice interval
        let xAxisMinRounded = Math.floor(xAxisMinAdjusted / niceInterval) * niceInterval
        let xAxisMaxRounded = Math.ceil(xAxisMaxAdjusted / niceInterval) * niceInterval
        let yAxisMinRounded = Math.floor(yAxisMinAdjusted / niceInterval) * niceInterval
        let yAxisMaxRounded = Math.ceil(yAxisMaxAdjusted / niceInterval) * niceInterval

        // Maintain equal pixel scaling after rounding
        const xRangeRounded = xAxisMaxRounded - xAxisMinRounded
        const yRangeRounded = yAxisMaxRounded - yAxisMinRounded

        if (xRangeRounded / gridWidth < yRangeRounded / gridHeight) {
          const targetXRange = (yRangeRounded / gridHeight) * gridWidth
          const xExpand = (targetXRange - xRangeRounded) / 2
          xAxisMinRounded -= Math.ceil(xExpand / niceInterval) * niceInterval
          xAxisMaxRounded += Math.ceil(xExpand / niceInterval) * niceInterval
        } else {
          const targetYRange = (xRangeRounded / gridWidth) * gridHeight
          const yExpand = (targetYRange - yRangeRounded) / 2
          yAxisMinRounded -= Math.ceil(yExpand / niceInterval) * niceInterval
          yAxisMaxRounded += Math.ceil(yExpand / niceInterval) * niceInterval
        }

        chartInstance1.current.setOption({
          backgroundColor: 'transparent',
          grid: {
            left: gridLeft,
            right: gridRight,
            top: gridTop,
            bottom: gridBottom,
            containLabel: false
          },
          xAxis: {
            type: 'value',
            name: `Z'{sub|real} / ${data.xUnit}`,
            nameLocation: 'middle',
            nameGap: 40,
            nameTextStyle: {
              color: '#cfd2d6',
              fontSize: 12,
              fontFamily: 'Roboto',
              rich: {
                sub: {
                  fontSize: 9,
                  verticalAlign: 'bottom',
                  padding: [3, 0, 0, 0]
                }
              }
            },
            axisLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.15)' } },
            axisLabel: {
              color: '#999',
              fontFamily: 'Roboto',
              fontSize: 11,
              formatter: (value) => formatValue(value, 'mΩ')
            },
            splitLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.05)' } },
            min: xAxisMinRounded,
            max: xAxisMaxRounded,
            interval: niceInterval
          },
          yAxis: {
            type: 'value',
            name: `-Z''{sub|imag} / ${data.yUnit}`,
            nameLocation: 'middle',
            nameGap: 50,
            nameTextStyle: {
              color: '#cfd2d6',
              fontSize: 12,
              fontFamily: 'Roboto',
              rich: {
                sub: {
                  fontSize: 9,
                  verticalAlign: 'bottom',
                  padding: [3, 0, 0, 0]
                }
              }
            },
            axisLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.15)' } },
            axisLabel: {
              color: '#999',
              fontFamily: 'Roboto',
              fontSize: 11,
              formatter: (value) => formatValue(value, 'mΩ')
            },
            splitLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.05)' } },
            min: yAxisMinRounded,
            max: yAxisMaxRounded,
            interval: niceInterval
          },
          series: [
            {
              type: 'line',
              name: 'Nyquist',
              data: nyquistData,
              showSymbol: true,
              symbolSize: 4,
              symbol: 'circle',
              lineStyle: {
                color: '#ea5b0c',
                width: 3
              },
              itemStyle: {
                color: '#ea5b0c'
              },
              emphasis: {
                itemStyle: { color: '#ff7700', borderWidth: 2, borderColor: '#fff' }
              }
            },
            // Reference point at R_ac_25, 0
            {
              type: 'scatter',
              name: 'R_ac Point',
              data: [[data.R_ac_25, 0]],
              symbolSize: 8,
              symbol: 'circle',
              itemStyle: {
                color: '#0d7c99',
                borderColor: '#fff',
                borderWidth: 2
              },
              z: 100
            },
            // Reference point at R_ac_25 + R_pol_25 on the Nyquist curve
            {
              type: 'scatter',
              name: 'R_pol Point',
              data: [[rPolX, rPolY]],
              symbolSize: 8,
              symbol: 'circle',
              itemStyle: {
                color: '#4CAF50',
                borderColor: '#fff',
                borderWidth: 2
              },
              z: 100
            },
            // Vertical line at R_ac_25
            {
              type: 'line',
              markLine: {
                silent: true,
                symbol: 'none',
                label: {
                  show: true,
                  position: 'insideEndTop',
                  formatter: 'R{sub|ac}',
                  color: '#0d7c99',
                  fontSize: 10,
                  fontFamily: 'Roboto',
                  rich: {
                    sub: {
                      fontSize: 8,
                      verticalAlign: 'bottom',
                      padding: [2, 0, 0, 0]
                    }
                  }
                },
                lineStyle: {
                  type: 'dashed',
                  color: '#0d7c99',
                  width: 1.5
                },
                data: [{ xAxis: data.R_ac_25 }]
              }
            },
            // Vertical line at R_pol_25 (between R_ac and R_EIS)
            {
              type: 'line',
              markLine: {
                silent: true,
                symbol: 'none',
                label: {
                  show: true,
                  position: 'insideEndTop',
                  formatter: 'R{sub|ac} + R{sub|pol}',
                  color: '#4CAF50',
                  fontSize: 10,
                  fontFamily: 'Roboto',
                  rich: {
                    sub: {
                      fontSize: 8,
                      verticalAlign: 'bottom',
                      padding: [2, 0, 0, 0]
                    }
                  }
                },
                lineStyle: {
                  type: 'dashed',
                  color: '#4CAF50',
                  width: 1.5
                },
                data: [{ xAxis: data.R_ac_25 + (data.R_pol_25 || 0) }]
              }
            }
            // R_EIS line removed per user request
          ],
          tooltip: {
            trigger: 'axis',
            backgroundColor: 'rgba(45, 42, 38, 0.95)',
            borderColor: '#ea5b0c',
            borderWidth: 1,
            textStyle: { color: '#fff', fontFamily: 'Roboto', fontSize: 12 },
            formatter: (params) => {
              if (!params || params.length === 0) return ''

              let tooltipText = ''
              params.forEach((param, idx) => {
                if (param.componentSubType === 'scatter') {
                  // Reference point
                  tooltipText += `${param.seriesName}: ${formatValue(param.value[0], data.xUnit)}<br/>`
                } else if (param.componentSubType === 'line') {
                  // Main curve
                  const zReal = param.value[0]
                  const zImag = param.value[1]
                  tooltipText += `Z_real: ${formatValue(zReal, data.xUnit)}<br/>`
                  tooltipText += `Z_imag: ${formatValue(Math.abs(zImag), data.yUnit)}`
                }
              })

              return tooltipText
            }
          }
        })

        // Hide second plot
        if (chartInstance2.current) chartInstance2.current.dispose()
        if (plot2dRef2.current) plot2dRef2.current.style.display = 'none'

      } else {
        // === BODE PLOTS: Magnitude and Phase vs Frequency (two plots VERTICALLY) ===
        if (plot2dRef2.current) {
          plot2dRef2.current.style.display = 'block'
          // Ensure vertical layout for Bode plots
          plot2dRef1.current.parentElement.style.display = 'flex'
          plot2dRef1.current.parentElement.style.flexDirection = 'column'
        }

        // Calculate magnitude |Z| = sqrt(Z_real^2 + Z_imag^2)
        const magnitudeData = []
        const phaseData = []

        for (let i = 0; i < data.EIS_25_f.length; i++) {
          const f = data.EIS_25_f[i]
          const zReal = data.X[i]  // Z_real
          const zImag = -data.Y[i]  // Original Z_imag (Y is already negated)

          const magnitude = Math.sqrt(zReal * zReal + zImag * zImag)
          const phase = Math.atan2(zImag, zReal) * (180 / Math.PI)  // Convert to degrees

          magnitudeData.push([f, magnitude])
          phaseData.push([f, phase])
        }

        console.log('Bode magnitude data points:', magnitudeData.length)
        console.log('Bode phase data points:', phaseData.length)

        // PLOT 1: Magnitude vs Frequency
        if (chartInstance1.current) chartInstance1.current.dispose()
        chartInstance1.current = echarts.init(plot2dRef1.current)

        chartInstance1.current.setOption({
          backgroundColor: 'transparent',
          grid: { left: 70, right: 30, top: 20, bottom: 70 },
          xAxis: {
            type: 'log',
            name: 'Frequency / Hz',
            nameLocation: 'middle',
            nameGap: 40,
            nameTextStyle: { color: '#cfd2d6', fontSize: 12, fontFamily: 'Roboto' },
            axisLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.15)' } },
            axisLabel: {
              show: true,
              color: '#999',
              fontFamily: 'Roboto',
              fontSize: 11,
              showMinLabel: false,
              showMaxLabel: false
            },
            splitLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.05)' } }
          },
          yAxis: {
            type: 'value',
            name: '|Z| / mΩ',
            nameLocation: 'middle',
            nameGap: 50,
            nameTextStyle: { color: '#cfd2d6', fontSize: 12, fontFamily: 'Roboto' },
            axisLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.15)' } },
            axisLabel: {
              show: true,
              color: '#999',
              fontFamily: 'Roboto',
              fontSize: 11,
              showMinLabel: false,
              showMaxLabel: false
            },
            splitLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.05)' } }
          },
          series: [
            {
              type: 'line',
              name: 'Magnitude',
              data: magnitudeData,
              showSymbol: false,
              lineStyle: {
                color: '#ea5b0c',
                width: 3
              }
            }
          ],
          tooltip: {
            trigger: 'axis',
            backgroundColor: 'rgba(45, 42, 38, 0.95)',
            borderColor: '#ea5b0c',
            borderWidth: 1,
            textStyle: { color: '#fff', fontFamily: 'Roboto', fontSize: 12 },
            formatter: (params) => {
              if (!params || params.length === 0) return ''
              const param = params[0]

              const frequency = param.value[0]
              const magnitude = param.value[1]

              const freqRounded = frequency >= 1000 ? (frequency / 1000).toFixed(1) + 'k' :
                                  frequency >= 100 ? frequency.toFixed(0) :
                                  frequency >= 10 ? frequency.toFixed(1) :
                                  frequency.toFixed(2)
              const magRounded = formatValue(magnitude, 'mΩ')

              return `Frequency: ${freqRounded} Hz<br/>|Z|: ${magRounded} mΩ`
            }
          }
        })

        // PLOT 2: Phase vs Frequency
        if (chartInstance2.current) chartInstance2.current.dispose()
        chartInstance2.current = echarts.init(plot2dRef2.current)

        chartInstance2.current.setOption({
          backgroundColor: 'transparent',
          grid: { left: 70, right: 30, top: 20, bottom: 70 },
          xAxis: {
            type: 'log',
            name: 'Frequency / Hz',
            nameLocation: 'middle',
            nameGap: 40,
            nameTextStyle: { color: '#cfd2d6', fontSize: 12, fontFamily: 'Roboto' },
            axisLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.15)' } },
            axisLabel: {
              show: true,
              color: '#999',
              fontFamily: 'Roboto',
              fontSize: 11,
              showMinLabel: false,
              showMaxLabel: false
            },
            splitLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.05)' } }
          },
          yAxis: {
            type: 'value',
            name: 'Phase / °',
            nameLocation: 'middle',
            nameGap: 50,
            nameTextStyle: { color: '#cfd2d6', fontSize: 12, fontFamily: 'Roboto' },
            axisLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.15)' } },
            axisLabel: {
              show: true,
              color: '#999',
              fontFamily: 'Roboto',
              fontSize: 11,
              showMinLabel: false,
              showMaxLabel: false
            },
            splitLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.05)' } }
          },
          series: [
            {
              type: 'line',
              name: 'Phase',
              data: phaseData,
              showSymbol: false,
              lineStyle: {
                color: '#0d7c99',
                width: 3
              }
            }
          ],
          tooltip: {
            trigger: 'axis',
            backgroundColor: 'rgba(45, 42, 38, 0.95)',
            borderColor: '#0d7c99',
            borderWidth: 1,
            textStyle: { color: '#fff', fontFamily: 'Roboto', fontSize: 12 },
            formatter: (params) => {
              if (!params || params.length === 0) return ''
              const param = params[0]

              const frequency = param.value[0]
              const phase = param.value[1]

              const freqRounded = frequency >= 1000 ? (frequency / 1000).toFixed(1) + 'k' :
                                  frequency >= 100 ? frequency.toFixed(0) :
                                  frequency >= 10 ? frequency.toFixed(1) :
                                  frequency.toFixed(2)
              const phaseRounded = formatValue(phase, '°')

              return `Frequency: ${freqRounded} Hz<br/>Phase: ${phaseRounded}°`
            }
          }
        })
      }

      return
    }

    // Show second plot for other categories (DC impedance, energy, power)
    if (plot2dRef2.current) plot2dRef2.current.style.display = 'block'

    // === PLOT 1: Z vs X for ALL unique temperatures (e.g., Energy vs Power at each T_amb) ===
    // Extract unique temperatures from data.Y (like MATLAB's unique(T_amb_DCC))
    const uniqueTempsSet = new Set()
    data.Y.forEach(temp => {
      // Round to nearest integer to group similar temps (e.g., -24.9 and -25.1 both become -25)
      const roundedTemp = Math.round(temp)
      uniqueTempsSet.add(roundedTemp)
    })
    const uniqueTemps = Array.from(uniqueTempsSet).sort((a, b) => a - b)

    console.log(`🌡️ ${categoryName} unique temperatures:`, uniqueTemps)

    // Color palette for temperature curves (from cold to hot)
    const TEMP_COLORS = [
      '#0077b6',  // Cold blue (-25°C)
      '#00b4d8',  // Light blue (0°C)
      '#ea5b0c',  // Orange (25°C) - reference
      '#d62828',  // Red (40°C)
      '#9d0208',  // Dark red (hot)
      '#7b2cbf',  // Purple (extra)
      '#2d6a4f',  // Green (extra)
    ]

    // Create data slices for each unique temperature
    let tempSlices = []
    uniqueTemps.forEach((targetTemp, idx) => {
      const yTolerance = 3 // ±3°C tolerance
      const sliceData = []

      for (let i = 0; i < data.X.length; i++) {
        if (Math.abs(data.Y[i] - targetTemp) <= yTolerance) {
          sliceData.push({ x: data.X[i], z: data.Z[i] })
        }
      }

      console.log(`  Temp ${targetTemp}°C: found ${sliceData.length} raw points`)

      // Sort by X for smooth line
      sliceData.sort((a, b) => a.x - b.x)

      // Remove duplicates (same X value) - keep average
      const xMap = new Map()
      sliceData.forEach(d => {
        const xKey = d.x.toFixed(3)
        if (xMap.has(xKey)) {
          const existing = xMap.get(xKey)
          xMap.set(xKey, (existing + d.z) / 2)
        } else {
          xMap.set(xKey, d.z)
        }
      })

      const slice = Array.from(xMap.entries())
        .map(([xStr, z]) => [parseFloat(xStr), z])
        .sort((a, b) => a[0] - b[0])

      if (slice.length > 0) {
        tempSlices.push({
          temp: targetTemp,
          data: slice,
          color: TEMP_COLORS[idx % TEMP_COLORS.length],
          isReference: targetTemp === 25 // Highlight 25°C as reference
        })
      }
    })

    console.log(`${categoryName} temperature slices:`, tempSlices.map(s => `${s.temp}°C: ${s.data.length} points`))

    // For dc-resistance (DCIR), show only 25°C curve
    if (categoryName === 'dc-resistance') {
      tempSlices = tempSlices.filter(s => s.temp === 25)
      console.log(`${categoryName} filtered to 25°C only:`, tempSlices.length, 'slices')
    }

    // Legacy: single slice at Y0 for backwards compatibility
    const xSlice = tempSlices.find(s => s.temp === Math.round(data.Y0))?.data || []

    console.log(`${categoryName} X-slice (at Y=${data.Y0}):`, xSlice.length, 'points')

    // === PLOT 2: Z vs Y (Temperature) for ALL 4 spec point conditions ===
    // Helper function to create a temperature slice at a specific X value (Power or Current)
    // Uses tight tolerance (1%) for most categories, but looser for dc-resistance
    // because DCIR data points may not align exactly with spec point X values
    const defaultTolerance = categoryName === 'dc-resistance' ? 0.10 : 0.01 // 10% for dc-resistance, 1% for others
    const createYSliceAtX = (targetX, tolerance = defaultTolerance) => {
      if (!targetX || targetX === 0) return []

      const sliceData = []
      for (let i = 0; i < data.X.length; i++) {
        const xDiff = Math.abs(data.X[i] - targetX) / Math.max(Math.abs(targetX), 0.001)
        if (xDiff <= tolerance) {
          sliceData.push({ y: data.Y[i], z: data.Z[i] })
        }
      }

      // Sort by Y for smooth line
      sliceData.sort((a, b) => a.y - b.y)

      // Remove duplicates (same Y value) - keep average
      const yMap = new Map()
      sliceData.forEach(d => {
        const yKey = data.yUnit === '°C' ? Math.round(d.y * 10) / 10 : d.y.toFixed(3)
        if (yMap.has(yKey)) {
          const existing = yMap.get(yKey)
          yMap.set(yKey, (existing + d.z) / 2)
        } else {
          yMap.set(yKey, d.z)
        }
      })

      return Array.from(yMap.entries())
        .map(([yStr, z]) => [typeof yStr === 'string' ? parseFloat(yStr) : yStr, z])
        .sort((a, b) => a[0] - b[0])
    }

    // Create slices for all 4 spec point conditions (Energy/Power categories)
    const isEnergyPowerDomain = !data.xLabel || data.xLabel === 'Power'

    // Get the X values (Power or Current) for each condition
    const X_C10 = isEnergyPowerDomain ? data.P_C10 : data.I_C10
    const X_dc_25 = isEnergyPowerDomain ? data.P_dc_25 : data.I_dc_25
    const X_cont = isEnergyPowerDomain ? data.P_cont : data.I_cont
    const X_peak = isEnergyPowerDomain ? data.P_peak : data.I_peak

    // Create slices for each condition
    const specPointSlices = [
      { name: 'C/10', x: X_C10, data: createYSliceAtX(X_C10), color: '#ea5b0c' },
      { name: 'Standard', x: X_dc_25, data: createYSliceAtX(X_dc_25), color: '#0077b6' },
      { name: 'Continuous', x: X_cont, data: createYSliceAtX(X_cont), color: '#d62828' },
      { name: 'Peak', x: X_peak, data: createYSliceAtX(X_peak), color: '#48cae4' }
    ].filter(s => s.data.length > 0)

    console.log(`${categoryName} Spec Point Slices:`, specPointSlices.map(s => `${s.name}: ${s.data.length} points at X=${s.x?.toFixed(2)}`))

    // Legacy: single slice at X0 for backwards compatibility and other categories
    const ySlice = createYSliceAtX(data.X0)
    console.log(`${categoryName} Y-slice (at X=${data.X0}):`, ySlice.length, 'points')

    // Calculate dynamic axis limits with padding - using ALL temperature slices for Chart 1
    // Collect all X and Z values from all temperature slices
    const allXValues = []
    const allZValuesFromX = []
    tempSlices.forEach(slice => {
      slice.data.forEach(d => {
        allXValues.push(d[0])
        allZValuesFromX.push(d[1])
      })
    })

    const xMin = allXValues.length > 0 ? Math.min(...allXValues) : 0
    const xMax = allXValues.length > 0 ? Math.max(...allXValues) : 1
    const xRange = xMax - xMin

    // Collect all Y and Z values from ALL spec point slices for Chart 2
    const allYValues = []
    const allZValuesFromY = []
    specPointSlices.forEach(slice => {
      slice.data.forEach(d => {
        allYValues.push(d[0])
        allZValuesFromY.push(d[1])
      })
    })
    // Fallback to legacy ySlice if no spec point slices
    if (allYValues.length === 0) {
      ySlice.forEach(d => {
        allYValues.push(d[0])
        allZValuesFromY.push(d[1])
      })
    }

    const yMin = allYValues.length > 0 ? Math.min(...allYValues) : 0
    const yMax = allYValues.length > 0 ? Math.max(...allYValues) : 1
    const yRange = yMax - yMin

    // For logarithmic axes (power category), don't calculate padding normally
    let zMinChart1, zMaxChart1, zMinChart2, zMaxChart2
    if (categoryName === 'power') {
      // For log scale, use data min/max from ALL slices
      const minFromX = allZValuesFromX.length > 0 ? Math.min(...allZValuesFromX) : 0.1
      const maxFromX = allZValuesFromX.length > 0 ? Math.max(...allZValuesFromX) : 1000
      const minFromY = allZValuesFromY.length > 0 ? Math.min(...allZValuesFromY) : 0.1
      const maxFromY = allZValuesFromY.length > 0 ? Math.max(...allZValuesFromY) : 1000

      // Use same min/max scale for BOTH plots (synchronized Y-axes)
      const commonZMin = Math.min(minFromX, minFromY)
      const commonZMax = Math.max(maxFromX, maxFromY)

      // For log scale starting at 0: use a small positive value (e.g., 1 second as minimum)
      // This gives the visual effect of "starting near 0" on log scale
      zMinChart1 = 1  // Start at 1 second (log(1) = 0)
      zMaxChart1 = commonZMax * 1.1  // Add 10% headroom
      zMinChart2 = 1  // Same for both charts
      zMaxChart2 = commonZMax * 1.1

      console.log('Power category Z-axis ranges (synchronized):')
      console.log('Common range:', zMinChart1, '-', zMaxChart1, 's')
      console.log('Chart 1 data range:', minFromX.toFixed(1), '-', maxFromX.toFixed(1), `(${allZValuesFromX.length} points)`)
      console.log('Chart 2 data range:', minFromY.toFixed(1), '-', maxFromY.toFixed(1), `(${allZValuesFromY.length} points)`)

      // If no data found, check if filtering was too strict
      if (allZValuesFromX.length === 0) {
        console.warn('⚠️ No data points found for Chart 1.')
        console.warn('Available Y values:', data.Y.slice(0, 10))
      }
      if (allZValuesFromY.length === 0) {
        console.warn('⚠️ No data points found for Chart 2.')
        console.warn('Available X values:', data.X.slice(0, 10))
      }
    } else {
      // For non-log scales: Y-axis starts at 0 and uses same max for both plots
      const zMaxChart1Temp = allZValuesFromX.length > 0 ? Math.max(...allZValuesFromX) : 1
      const zMaxChart2Temp = allZValuesFromY.length > 0 ? Math.max(...allZValuesFromY) : 1

      // Use the common maximum for both charts
      const commonZMax = Math.max(zMaxChart1Temp, zMaxChart2Temp)

      zMinChart1 = 0
      zMaxChart1 = commonZMax + (commonZMax * 0.1) // Add 10% padding to max

      zMinChart2 = 0
      zMaxChart2 = commonZMax + (commonZMax * 0.1)

      console.log('Linear scale Z-axis ranges (both charts use same max):')
      console.log('Chart 1 data max:', zMaxChart1Temp, '-> axis max:', zMaxChart1)
      console.log('Chart 2 data max:', zMaxChart2Temp, '-> axis max:', zMaxChart2)
    }

    // === RENDER CHART 1: Z vs X for ALL temperatures ===
    if (chartInstance1.current) chartInstance1.current.dispose()
    chartInstance1.current = echarts.init(plot2dRef1.current)

    // Define spec point names for legend filtering
    const specPointNames = ['C/10', 'Standard', 'Continuous', 'Peak']
    const tempNames = tempSlices.map(s => `${s.temp}°C`)

    // For dc-resistance, show special legend with reference condition
    const resistanceLegendData = categoryName === 'dc-resistance' ? ['T = 25°C'] : tempNames

    chartInstance1.current.setOption({
      backgroundColor: 'transparent',
      grid: { left: 70, right: 30, top: 50, bottom: 55 },
      legend: [
        // Row 1: Spec Points (scatter markers) - only for energy/power
        {
          show: categoryName === 'energy' || categoryName === 'power',
          top: 0,
          left: 'center',
          orient: 'horizontal',
          data: specPointNames,
          textStyle: { color: '#cfd2d6', fontSize: 9, fontFamily: 'Roboto' },
          itemWidth: 10,
          itemHeight: 10,
          itemGap: 12
        },
        // Row 2: Temperature curves - show for all categories (including dc-resistance with single temp)
        {
          show: tempSlices.length >= 1,
          top: (categoryName === 'energy' || categoryName === 'power') ? 18 : 0,
          left: 'center',
          orient: 'horizontal',
          data: resistanceLegendData,
          textStyle: { color: '#cfd2d6', fontSize: 9, fontFamily: 'Roboto' },
          itemWidth: 18,
          itemHeight: 3,
          itemGap: 8
        }
      ],
      xAxis: {
        type: 'value',
        name: `${data.xLabel} / ${data.xUnit}`,
        nameLocation: 'middle',
        nameGap: 40,
        min: xMin - xRange * 0.05,
        max: xMax + xRange * 0.05,
        nameTextStyle: { color: '#cfd2d6', fontSize: 12, fontFamily: 'Roboto' },
        axisLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.15)' } },
        axisLabel: {
          show: true,
          color: '#999',
          fontFamily: 'Roboto',
          fontSize: 11,
          showMinLabel: false,
          showMaxLabel: false
        },
        splitLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.05)' } }
      },
      yAxis: {
        type: categoryName === 'power' ? 'log' : 'value',
        name: categoryName === 'dc-resistance' ? 'DCIR / mΩ' : `${data.zLabel} / ${data.zUnit}`,
        nameLocation: 'middle',
        nameGap: 50,
        logBase: categoryName === 'power' ? 10 : undefined,
        min: categoryName === 'power' ? (zMinChart1 > 0 ? zMinChart1 : 0.1) : zMinChart1,
        max: categoryName === 'power' ? (zMaxChart1 > zMinChart1 ? zMaxChart1 : 10000) : zMaxChart1,
        nameTextStyle: { color: '#cfd2d6', fontSize: 12, fontFamily: 'Roboto' },
        axisLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.15)' } },
        axisLabel: {
          show: true,
          color: '#999',
          fontFamily: 'Roboto',
          fontSize: 11,
          showMinLabel: false,
          showMaxLabel: false,
          formatter: categoryName === 'power' ? (value) => {
            if (value >= 1000) return (value / 1000).toFixed(0) + 'k'
            if (value >= 100) return value.toFixed(0)
            if (value >= 10) return value.toFixed(0)
            if (value >= 1) return value.toFixed(1)
            return value.toFixed(2)
          } : undefined
        },
        minorTick: categoryName === 'power' ? { show: true, splitNumber: 5 } : undefined,
        minorSplitLine: categoryName === 'power' ? { show: true, lineStyle: { color: 'rgba(255, 255, 255, 0.02)' } } : undefined,
        splitLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.05)' } }
      },
      series: [
        // === Temperature curves: one line per unique temperature ===
        ...tempSlices.map((slice, idx) => ({
          type: 'line',
          name: categoryName === 'dc-resistance' ? `T = ${slice.temp}°C` : `${slice.temp}°C`,
          data: slice.data,
          lineStyle: {
            color: slice.color,
            width: 3,
            type: 'solid'
          },
          symbol: 'circle',
          symbolSize: 6,
          itemStyle: { color: slice.color },
          smooth: false,
          emphasis: {
            lineStyle: { width: 4 },
            itemStyle: { borderWidth: 2, borderColor: '#fff' }
          }
        })),
        // === 4 Spec Points for Experimental Conditions - DOMAIN SENSITIVE ===
        // Energy category: Energy-Power domain (Power vs Energy) OR Capacity-Current domain (Current vs Capacity)
        // Determine which domain we're in
        ...(categoryName === 'energy' ? (() => {
          const isEnergyPowerDomain = !data.xLabel || data.xLabel === 'Power'
          console.log('\n=== ENERGY 2D SPEC POINTS DEBUG ===')
          console.log('Domain:', isEnergyPowerDomain ? 'Energy-Power' : 'Capacity-Current')
          if (isEnergyPowerDomain) {
            console.log('C/10:       [P=' + data.P_C10 + ' ' + data.xUnit + ', E=' + data.E_C10 + ' ' + data.zUnit + ']')
            console.log('Standard:   [P=' + data.P_dc_25 + ' ' + data.xUnit + ', E=' + data.E_dc_25 + ' ' + data.zUnit + ']')
            console.log('Continuous: [P=' + data.P_cont + ' ' + data.xUnit + ', E=' + data.E_cont + ' ' + data.zUnit + ']')
            console.log('Peak:       [P=' + data.P_peak + ' ' + data.xUnit + ', E=' + data.E_peak + ' ' + data.zUnit + ']')
          } else {
            console.log('C/10:       [I=' + data.I_C10 + ' ' + data.xUnit + ', C=' + data.C_C10 + ' ' + data.zUnit + ']')
            console.log('Standard:   [I=' + data.I_dc_25 + ' ' + data.xUnit + ', C=' + data.C_dc_25 + ' ' + data.zUnit + ']')
            console.log('Continuous: [I=' + data.I_cont + ' ' + data.xUnit + ', C=' + data.C_cont + ' ' + data.zUnit + ']')
            console.log('Peak:       [I=' + data.I_peak + ' ' + data.xUnit + ', C=' + data.C_peak + ' ' + data.zUnit + ']')
          }

          if (isEnergyPowerDomain) {
            // Energy-Power domain: [[Power, Energy]]
            return [
              // C/10 (orange)
              ...(data.P_C10 !== undefined && data.E_C10 !== undefined ? [{
                type: 'scatter',
                name: 'C/10',
                data: [[data.P_C10, data.E_C10]],
                symbol: 'circle',
                symbolSize: 12,
                itemStyle: { color: '#ea5b0c', borderColor: '#fff', borderWidth: 2 },
                z: 10
              }] : []),
              // Standard (blue)
              ...(data.P_dc_25 !== undefined && data.E_dc_25 !== undefined ? [{
                type: 'scatter',
                name: 'Standard',
                data: [[data.P_dc_25, data.E_dc_25]],
                symbol: 'circle',
                symbolSize: 12,
                itemStyle: { color: '#0077b6', borderColor: '#fff', borderWidth: 2 },
                z: 10
              }] : []),
              // Continuous (red)
              ...(data.P_cont !== undefined && data.E_cont !== undefined ? [{
                type: 'scatter',
                name: 'Continuous',
                data: [[data.P_cont, data.E_cont]],
                symbol: 'circle',
                symbolSize: 12,
                itemStyle: { color: '#d62828', borderColor: '#fff', borderWidth: 2 },
                z: 10
              }] : []),
              // Peak (cyan)
              ...(data.P_peak !== undefined && data.E_peak !== undefined ? [{
                type: 'scatter',
                name: 'Peak',
                data: [[data.P_peak, data.E_peak]],
                symbol: 'circle',
                symbolSize: 12,
                itemStyle: { color: '#48cae4', borderColor: '#fff', borderWidth: 2 },
                z: 10
              }] : [])
            ]
          } else {
            // Capacity-Current domain: [[Current, Capacity]]
            return [
              // C/10 (orange)
              ...(data.I_C10 !== undefined && data.C_C10 !== undefined ? [{
                type: 'scatter',
                name: 'C/10',
                data: [[data.I_C10, data.C_C10]],
                symbol: 'circle',
                symbolSize: 12,
                itemStyle: { color: '#ea5b0c', borderColor: '#fff', borderWidth: 2 },
                z: 10
              }] : []),
              // Standard (blue)
              ...(data.I_dc_25 !== undefined && data.C_dc_25 !== undefined ? [{
                type: 'scatter',
                name: 'Standard',
                data: [[data.I_dc_25, data.C_dc_25]],
                symbol: 'circle',
                symbolSize: 12,
                itemStyle: { color: '#0077b6', borderColor: '#fff', borderWidth: 2 },
                z: 10
              }] : []),
              // Continuous (red)
              ...(data.I_cont !== undefined && data.C_cont !== undefined ? [{
                type: 'scatter',
                name: 'Continuous',
                data: [[data.I_cont, data.C_cont]],
                symbol: 'circle',
                symbolSize: 12,
                itemStyle: { color: '#d62828', borderColor: '#fff', borderWidth: 2 },
                z: 10
              }] : []),
              // Peak (cyan)
              ...(data.I_peak !== undefined && data.C_peak !== undefined ? [{
                type: 'scatter',
                name: 'Peak',
                data: [[data.I_peak, data.C_peak]],
                symbol: 'circle',
                symbolSize: 12,
                itemStyle: { color: '#48cae4', borderColor: '#fff', borderWidth: 2 },
                z: 10
              }] : [])
            ]
          }
        })() : []),

        // Power category: Power-Duration domain (Power vs Duration) OR Current-Duration domain (Current vs Duration)
        ...(categoryName === 'power' ? (() => {
          const isPowerDomain = !data.xLabel || data.xLabel === 'Power'
          console.log('\n=== POWER 2D SPEC POINTS DEBUG ===')
          console.log('Domain:', isPowerDomain ? 'Power-Duration' : 'Current-Duration')
          if (isPowerDomain) {
            console.log('C/10:       [P=' + data.P_C10 + ' ' + data.xUnit + ', t_max=' + data.t_max_C10 + ' ' + data.zUnit + ']')
            console.log('Standard:   [P=' + data.P_dc_25 + ' ' + data.xUnit + ', t_max=' + data.t_max_dc_25 + ' ' + data.zUnit + ']')
            console.log('Continuous: [P=' + data.P_cont + ' ' + data.xUnit + ', t_max=' + data.t_max_cont + ' ' + data.zUnit + ']')
            console.log('Peak:       [P=' + data.P_peak + ' ' + data.xUnit + ', t_max=' + data.t_max_peak + ' ' + data.zUnit + ']')
          } else {
            console.log('C/10:       [I=' + data.I_C10 + ' ' + data.xUnit + ', t_max=' + data.t_max_C10 + ' ' + data.zUnit + ']')
            console.log('Standard:   [I=' + data.I_dc_25 + ' ' + data.xUnit + ', t_max=' + data.t_max_dc_25 + ' ' + data.zUnit + ']')
            console.log('Continuous: [I=' + data.I_cont + ' ' + data.xUnit + ', t_max=' + data.t_max_cont + ' ' + data.zUnit + ']')
            console.log('Peak:       [I=' + data.I_peak + ' ' + data.xUnit + ', t_max=' + data.t_max_peak + ' ' + data.zUnit + ']')
          }

          if (isPowerDomain) {
            // Power-Duration domain: [[Power, Duration]]
            return [
              // C/10 (orange)
              ...(data.P_C10 !== undefined && data.t_max_C10 !== undefined ? [{
                type: 'scatter',
                name: 'C/10',
                data: [[data.P_C10, data.t_max_C10]],
                symbol: 'circle',
                symbolSize: 12,
                itemStyle: { color: '#ea5b0c', borderColor: '#fff', borderWidth: 2 },
                z: 10
              }] : []),
              // Standard (blue)
              ...(data.P_dc_25 !== undefined && data.t_max_dc_25 !== undefined ? [{
                type: 'scatter',
                name: 'Standard',
                data: [[data.P_dc_25, data.t_max_dc_25]],
                symbol: 'circle',
                symbolSize: 12,
                itemStyle: { color: '#0077b6', borderColor: '#fff', borderWidth: 2 },
                z: 10
              }] : []),
              // Continuous (red)
              ...(data.P_cont !== undefined && data.t_max_cont !== undefined ? [{
                type: 'scatter',
                name: 'Continuous',
                data: [[data.P_cont, data.t_max_cont]],
                symbol: 'circle',
                symbolSize: 12,
                itemStyle: { color: '#d62828', borderColor: '#fff', borderWidth: 2 },
                z: 10
              }] : []),
              // Peak (cyan)
              ...(data.P_peak !== undefined && data.t_max_peak !== undefined ? [{
                type: 'scatter',
                name: 'Peak',
                data: [[data.P_peak, data.t_max_peak]],
                symbol: 'circle',
                symbolSize: 12,
                itemStyle: { color: '#48cae4', borderColor: '#fff', borderWidth: 2 },
                z: 10
              }] : [])
            ]
          } else {
            // Current-Duration domain: [[Current, Duration]]
            return [
              // C/10 (orange)
              ...(data.I_C10 !== undefined && data.t_max_C10 !== undefined ? [{
                type: 'scatter',
                name: 'C/10',
                data: [[data.I_C10, data.t_max_C10]],
                symbol: 'circle',
                symbolSize: 12,
                itemStyle: { color: '#ea5b0c', borderColor: '#fff', borderWidth: 2 },
                z: 10
              }] : []),
              // Standard (blue)
              ...(data.I_dc_25 !== undefined && data.t_max_dc_25 !== undefined ? [{
                type: 'scatter',
                name: 'Standard',
                data: [[data.I_dc_25, data.t_max_dc_25]],
                symbol: 'circle',
                symbolSize: 12,
                itemStyle: { color: '#0077b6', borderColor: '#fff', borderWidth: 2 },
                z: 10
              }] : []),
              // Continuous (red)
              ...(data.I_cont !== undefined && data.t_max_cont !== undefined ? [{
                type: 'scatter',
                name: 'Continuous',
                data: [[data.I_cont, data.t_max_cont]],
                symbol: 'circle',
                symbolSize: 12,
                itemStyle: { color: '#d62828', borderColor: '#fff', borderWidth: 2 },
                z: 10
              }] : []),
              // Peak (cyan)
              ...(data.I_peak !== undefined && data.t_max_peak !== undefined ? [{
                type: 'scatter',
                name: 'Peak',
                data: [[data.I_peak, data.t_max_peak]],
                symbol: 'circle',
                symbolSize: 12,
                itemStyle: { color: '#48cae4', borderColor: '#fff', borderWidth: 2 },
                z: 10
              }] : [])
            ]
          }
        })() : []),

        // Fallback single point for other categories (DC impedance)
        ...(categoryName !== 'energy' && categoryName !== 'power' ? [{
          type: 'scatter',
          name: 'Spec Point',
          data: [[data.X0, data.Z0]],
          symbol: 'circle',
          symbolSize: 14,
          itemStyle: {
            color: '#fff',
            borderColor: '#ea5b0c',
            borderWidth: 3
          },
          z: 10
        }] : [])
      ],
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(45, 42, 38, 0.95)',
        borderColor: '#ea5b0c',
        borderWidth: 1,
        textStyle: { color: '#fff', fontFamily: 'Roboto', fontSize: 12 },
        axisPointer: {
          type: 'line',
          lineStyle: { color: 'rgba(255, 255, 255, 0.3)', type: 'dashed' }
        },
        formatter: (params) => {
          if (!params || params.length === 0) return ''

          // First line: X-axis value with unit
          let tooltipText = `<strong>${data.xLabel}: ${formatValue(params[0].value[0], data.xUnit)} ${data.xUnit}</strong><br/>`

          // Show all series values at this X position
          params.forEach((param) => {
            const value = param.value[1]
            const color = param.color
            tooltipText += `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${color};margin-right:5px;"></span>${param.seriesName}: ${formatValue(value, data.zUnit)} ${data.zUnit}<br/>`
          })

          return tooltipText
        }
      }
    })

    // === RENDER CHART 2: Z vs Y (Temperature) for ALL 4 spec point conditions ===
    if (chartInstance2.current) chartInstance2.current.dispose()
    chartInstance2.current = echarts.init(plot2dRef2.current)

    // Get Z values at Y0=25°C for each spec point condition (for scatter markers)
    const getZAtY0 = (sliceData, y0 = 25) => {
      if (!sliceData || sliceData.length === 0) return null
      // Find closest point to y0
      let closest = sliceData[0]
      let minDist = Math.abs(sliceData[0][0] - y0)
      sliceData.forEach(d => {
        const dist = Math.abs(d[0] - y0)
        if (dist < minDist) {
          minDist = dist
          closest = d
        }
      })
      return minDist <= 5 ? closest[1] : null  // Return value if within 5°C
    }

    chartInstance2.current.setOption({
      backgroundColor: 'transparent',
      grid: { left: 70, right: 30, top: 50, bottom: 55 },
      legend: [
        // Row 1: Spec Points (scatter markers at 25°C)
        {
          show: (categoryName === 'energy' || categoryName === 'power') && specPointSlices.length > 1,
          top: 0,
          left: 'center',
          orient: 'horizontal',
          data: specPointSlices.map(s => `${s.name} (25°C)`),
          textStyle: { color: '#cfd2d6', fontSize: 9, fontFamily: 'Roboto' },
          itemWidth: 10,
          itemHeight: 10,
          itemGap: 12
        },
        // Row 2: Condition curves (same colors as spec points)
        {
          show: (categoryName === 'energy' || categoryName === 'power') && specPointSlices.length > 1,
          top: 18,
          left: 'center',
          orient: 'horizontal',
          data: specPointSlices.map(s => s.name),
          textStyle: { color: '#cfd2d6', fontSize: 9, fontFamily: 'Roboto' },
          itemWidth: 18,
          itemHeight: 3,
          itemGap: 12
        }
      ],
      xAxis: {
        type: 'value',
        name: `${data.yLabel} / ${data.yUnit}`,
        nameLocation: 'middle',
        nameGap: 40,
        min: yMin - yRange * 0.05,
        max: yMax + yRange * 0.05,
        nameTextStyle: { color: '#cfd2d6', fontSize: 12, fontFamily: 'Roboto' },
        axisLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.15)' } },
        axisLabel: {
          show: true,
          color: '#999',
          fontFamily: 'Roboto',
          fontSize: 11,
          showMinLabel: false,
          showMaxLabel: false
        },
        splitLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.05)' } }
      },
      yAxis: {
        type: categoryName === 'power' ? 'log' : 'value',
        name: categoryName === 'dc-resistance' ? 'DCIR / mΩ' : `${data.zLabel} / ${data.zUnit}`,
        nameLocation: 'middle',
        nameGap: 50,
        logBase: categoryName === 'power' ? 10 : undefined,
        min: categoryName === 'power' ? (zMinChart2 > 0 ? zMinChart2 : 0.1) : zMinChart2,
        max: categoryName === 'power' ? (zMaxChart2 > zMinChart2 ? zMaxChart2 : 10000) : zMaxChart2,
        nameTextStyle: { color: '#cfd2d6', fontSize: 12, fontFamily: 'Roboto' },
        axisLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.15)' } },
        axisLabel: {
          show: true,
          color: '#999',
          fontFamily: 'Roboto',
          fontSize: 11,
          showMinLabel: false,
          showMaxLabel: false,
          formatter: categoryName === 'power' ? (value) => {
            if (value >= 1000) return (value / 1000).toFixed(0) + 'k'
            if (value >= 100) return value.toFixed(0)
            if (value >= 10) return value.toFixed(0)
            if (value >= 1) return value.toFixed(1)
            return value.toFixed(2)
          } : undefined
        },
        minorTick: categoryName === 'power' ? { show: true, splitNumber: 5 } : undefined,
        minorSplitLine: categoryName === 'power' ? { show: true, lineStyle: { color: 'rgba(255, 255, 255, 0.02)' } } : undefined,
        splitLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.05)' } }
      },
      series: [
        // === 4 Spec Point curves (for energy/power categories) ===
        ...((categoryName === 'energy' || categoryName === 'power') ? specPointSlices.map(slice => ({
          type: 'line',
          name: slice.name,
          data: slice.data,
          lineStyle: { color: slice.color, width: 3 },
          symbol: 'circle',
          symbolSize: 6,
          itemStyle: { color: slice.color },
          smooth: false,
          emphasis: {
            lineStyle: { width: 4 },
            itemStyle: { borderWidth: 2, borderColor: '#fff' }
          }
        })) : [{
          // Fallback single line for other categories
          type: 'line',
          name: `${data.zLabel} vs ${data.yLabel}`,
          data: ySlice,
          lineStyle: { color: '#0d7c99', width: 3 },
          symbol: 'circle',
          symbolSize: 6,
          itemStyle: { color: '#0d7c99' },
          smooth: false
        }]),
        // === 4 Spec Points at T=25°C (scatter markers) ===
        // Use Z values from the slice data at Y=25°C so points lie exactly on the lines
        ...((categoryName === 'energy' || categoryName === 'power') ? specPointSlices.map(slice => {
          const zAtY0 = getZAtY0(slice.data)
          return zAtY0 !== null ? {
            type: 'scatter',
            name: `${slice.name} (25°C)`,
            data: [[25, zAtY0]],
            symbol: 'circle',
            symbolSize: 12,
            itemStyle: { color: slice.color, borderColor: '#fff', borderWidth: 2 },
            z: 10
          } : null
        }).filter(Boolean) : []),
        // Fallback single point for other categories (voltage, impedance, resistance)
        ...(categoryName !== 'energy' && categoryName !== 'power' ? [{
          type: 'scatter',
          name: 'Spec Point',
          data: [[data.Y0, data.Z0]],
          symbol: 'circle',
          symbolSize: 14,
          itemStyle: {
            color: '#fff',
            borderColor: '#0d7c99',
            borderWidth: 3
          },
          z: 10
        }] : [])
      ],
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(45, 42, 38, 0.95)',
        borderColor: '#0d7c99',
        borderWidth: 1,
        textStyle: { color: '#fff', fontFamily: 'Roboto', fontSize: 12 },
        axisPointer: {
          type: 'line',
          lineStyle: { color: 'rgba(255, 255, 255, 0.3)', type: 'dashed' }
        },
        formatter: (params) => {
          if (!params || params.length === 0) return ''

          // First line: X-axis value (Ambient Temperature) with unit
          let tooltipText = `<strong>${data.yLabel}: ${formatValue(params[0].value[0], data.yUnit)} ${data.yUnit}</strong><br/>`

          // Show all series values at this temperature
          params.forEach((param) => {
            const value = param.value[1]
            const color = param.color
            tooltipText += `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${color};margin-right:5px;"></span>${param.seriesName}: ${formatValue(value, data.zUnit)} ${data.zUnit}<br/>`
          })

          return tooltipText
        }
      }
    })
  }

  // === MATLAB-STYLE HELPER: Delaunay Triangulation ===
  // Implements 2D Delaunay triangulation for scattered points
  // Based on Bowyer-Watson algorithm with improved edge handling
  function delaunay2D(points) {
    // points = [{x, y}, ...]
    // Returns array of triangles: [{a, b, c}, ...] where a,b,c are indices

    if (points.length < 3) return []

    // Normalize points to [0, 1] range for better numerical stability
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity
    points.forEach(p => {
      minX = Math.min(minX, p.x)
      maxX = Math.max(maxX, p.x)
      minY = Math.min(minY, p.y)
      maxY = Math.max(maxY, p.y)
    })

    const dx = maxX - minX || 1
    const dy = maxY - minY || 1

    // Normalize points
    const normalizedPoints = points.map(p => ({
      x: (p.x - minX) / dx,
      y: (p.y - minY) / dy
    }))

    // Create MUCH larger super-triangle to ensure all points are well inside
    // This prevents edge triangles from being lost
    const superTriangle = [
      { x: -10, y: -10 },      // Far bottom-left
      { x: 0.5, y: 20 },       // Far top-center
      { x: 20, y: -10 }        // Far bottom-right
    ]

    // Add super-triangle vertices to points
    const allPoints = [...normalizedPoints, ...superTriangle]
    const n = normalizedPoints.length

    // Initialize with super-triangle
    let triangles = [{ a: n, b: n + 1, c: n + 2 }]

    // Helper: Check if point is inside circumcircle
    const inCircumcircle = (px, py, tri) => {
      const ax = allPoints[tri.a].x, ay = allPoints[tri.a].y
      const bx = allPoints[tri.b].x, by = allPoints[tri.b].y
      const cx = allPoints[tri.c].x, cy = allPoints[tri.c].y

      const d = 2 * (ax * (by - cy) + bx * (cy - ay) + cx * (ay - by))
      if (Math.abs(d) < 1e-14) return false

      const ux = ((ax*ax + ay*ay) * (by - cy) + (bx*bx + by*by) * (cy - ay) + (cx*cx + cy*cy) * (ay - by)) / d
      const uy = ((ax*ax + ay*ay) * (cx - bx) + (bx*bx + by*by) * (ax - cx) + (cx*cx + cy*cy) * (bx - ax)) / d
      const r2 = (ax - ux) * (ax - ux) + (ay - uy) * (ay - uy)
      const dist2 = (px - ux) * (px - ux) + (py - uy) * (py - uy)

      // Use small epsilon for numerical stability (point ON circle should be considered inside)
      return dist2 < r2 + 1e-10
    }

    // Add points one by one (shuffled order can improve robustness, but we keep original)
    for (let i = 0; i < n; i++) {
      const px = normalizedPoints[i].x, py = normalizedPoints[i].y

      // Find all triangles whose circumcircle contains this point
      const badTriangles = []
      const goodTriangles = []

      triangles.forEach(tri => {
        if (inCircumcircle(px, py, tri)) {
          badTriangles.push(tri)
        } else {
          goodTriangles.push(tri)
        }
      })

      // Find boundary polygon of bad triangles
      const edges = []
      badTriangles.forEach(tri => {
        edges.push({ a: tri.a, b: tri.b })
        edges.push({ a: tri.b, b: tri.c })
        edges.push({ a: tri.c, b: tri.a })
      })

      // Keep only edges that are not shared (boundary edges)
      const boundaryEdges = edges.filter((e1, idx) => {
        const duplicate = edges.some((e2, idx2) => {
          if (idx === idx2) return false
          return (e1.a === e2.b && e1.b === e2.a) || (e1.a === e2.a && e1.b === e2.b)
        })
        return !duplicate
      })

      // Create new triangles from boundary edges to the new point
      boundaryEdges.forEach(edge => {
        goodTriangles.push({ a: edge.a, b: edge.b, c: i })
      })

      triangles = goodTriangles
    }

    // Remove triangles that share vertices with super-triangle
    triangles = triangles.filter(tri => {
      return tri.a < n && tri.b < n && tri.c < n
    })

    console.log(`Delaunay: ${points.length} points -> ${triangles.length} triangles`)

    return triangles
  }

  // === MATLAB-STYLE HELPER: Scattered Interpolation ===
  // Like MATLAB's scatteredInterpolant - uses barycentric interpolation
  function createScatteredInterpolant(points, values) {
    // points = [{x, y}, ...], values = [z1, z2, ...]
    // Returns function(x, y) => interpolated z

    const triangles = delaunay2D(points)

    return function(x, y) {
      // Find triangle containing (x, y)
      for (const tri of triangles) {
        const p0 = points[tri.a], p1 = points[tri.b], p2 = points[tri.c]
        const v0 = values[tri.a], v1 = values[tri.b], v2 = values[tri.c]

        // Compute barycentric coordinates
        const denom = (p1.y - p2.y) * (p0.x - p2.x) + (p2.x - p1.x) * (p0.y - p2.y)
        if (Math.abs(denom) < 1e-12) continue

        const w0 = ((p1.y - p2.y) * (x - p2.x) + (p2.x - p1.x) * (y - p2.y)) / denom
        const w1 = ((p2.y - p0.y) * (x - p2.x) + (p0.x - p2.x) * (y - p2.y)) / denom
        const w2 = 1 - w0 - w1

        // Check if point is inside triangle
        if (w0 >= -0.001 && w1 >= -0.001 && w2 >= -0.001) {
          return v0 * w0 + v1 * w1 + v2 * w2
        }
      }

      // Point outside triangulation - return nearest value
      let minDist = Infinity, nearest = values[0]
      points.forEach((p, i) => {
        const dist = (p.x - x) * (p.x - x) + (p.y - y) * (p.y - y)
        if (dist < minDist) {
          minDist = dist
          nearest = values[i]
        }
      })
      return nearest
    }
  }

  function render3DPlot(data, categoryName) {
    if (!plot3dRef.current) {
      console.warn('plot3dRef.current is null')
      return
    }

    if (!window.Plotly) {
      console.warn('⚠️ Plotly not available. Make sure it is loaded via CDN in index.html')
      return
    }

    // AC Impedance should never reach 3D mode
    if (categoryName === 'ac-impedance') {
      console.warn('⚠️ AC Impedance does not support 3D mode')
      return
    }

    // Validate data structure
    if (!data.X || !data.Y || !data.Z || data.X.length === 0) {
      console.warn('⚠️ Invalid 3D data structure:', {
        hasX: !!data.X,
        hasY: !!data.Y,
        hasZ: !!data.Z,
        lengthX: data.X?.length
      })
      return
    }

    console.log('=== render3DPlot called ===')
    console.log('Data X length:', data.X?.length)
    console.log('Data Y length:', data.Y?.length)
    console.log('Data Z length:', data.Z?.length)
    console.log('First 5 X values:', data.X?.slice(0, 5))
    console.log('First 5 Y values:', data.Y?.slice(0, 5))
    console.log('First 5 Z values:', data.Z?.slice(0, 5))

    // === BUILD 3D SURFACE FROM SCATTERED DATA ===
    // Group data by temperature to create regular grid rows
    const tempGroups = new Map()
    for (let i = 0; i < data.X.length; i++) {
      const tempKey = Math.round(data.Y[i] * 10) / 10  // Round to 0.1°C precision
      if (!tempGroups.has(tempKey)) {
        tempGroups.set(tempKey, [])
      }
      tempGroups.get(tempKey).push({ power: data.X[i], energy: data.Z[i] })
    }

    // Sort temperature keys
    const uniqueTemp = Array.from(tempGroups.keys()).sort((a, b) => a - b)

    // For each temperature, sort points by power and remove duplicates
    const gridData = []
    uniqueTemp.forEach(temp => {
      const points = tempGroups.get(temp)
      // Remove duplicate power values by averaging
      const powerMap = new Map()
      points.forEach(p => {
        const key = p.power.toFixed(2)
        if (powerMap.has(key)) {
          const existing = powerMap.get(key)
          powerMap.set(key, (existing + p.energy) / 2)
        } else {
          powerMap.set(key, p.energy)
        }
      })
      const cleanedPoints = Array.from(powerMap.entries())
        .map(([powerStr, energy]) => ({ power: parseFloat(powerStr), energy }))
        .sort((a, b) => a.power - b.power)

      gridData.push({ temp, points: cleanedPoints })
    })

    // Strategy: Use ALL unique power values across all temperatures
    // This gives us the full picture, we'll interpolate missing values
    const allPowers = new Set()
    gridData.forEach(g => {
      g.points.forEach(p => {
        allPowers.add(p.power)
      })
    })
    const allPowersSorted = Array.from(allPowers).sort((a, b) => a - b)

    console.log('=== Power filtering ===')
    console.log('Total unique powers:', allPowersSorted.length)
    if (allPowersSorted.length > 0) {
      console.log('Power range:', allPowersSorted[0].toFixed(2), '-', allPowersSorted[allPowersSorted.length-1].toFixed(2))
    }

    // Build grid using ALL powers, with interpolation for missing values
    // NO EXTRAPOLATION - only interpolate within measured data range
    console.log('=== Power filtering ===')
    console.log('Total unique powers:', allPowersSorted.length)
    if (allPowersSorted.length > 0) {
      console.log('Power range:', allPowersSorted[0].toFixed(2), '-', allPowersSorted[allPowersSorted.length-1].toFixed(2))
    }

    const gridZ = []
    let interpolationCount = 0
    let nullCount = 0

    gridData.forEach(g => {
      const row = []
      allPowersSorted.forEach(targetPower => {
        // Find exact match (with small tolerance for floating point)
        const exact = g.points.find(p => Math.abs(p.power - targetPower) < 0.1)
        if (exact) {
          row.push(exact.energy)
        } else {
          // Linear interpolation between surrounding points
          const before = g.points.filter(p => p.power < targetPower).slice(-1)[0]
          const after = g.points.filter(p => p.power > targetPower)[0]

          if (before && after) {
            // Both neighbors exist - interpolate
            const t = (targetPower - before.power) / (after.power - before.power)
            const interpolated = before.energy + t * (after.energy - before.energy)
            row.push(interpolated)
            interpolationCount++
          } else {
            // No neighbors on both sides - outside measured range, use null for NO EXTRAPOLATION
            // This prevents misleading values outside the actual data range
            row.push(null)
            nullCount++
          }
        }
      })
      gridZ.push(row)
    })

    console.log(`Grid construction (${categoryName}): ${gridData.length} temperature rows, ${interpolationCount} interpolated cells, ${nullCount} null cells (no extrapolation)`)

    const uniquePowerValues = allPowersSorted

    console.log('=== 3D Grid Structure ===')
    console.log('Unique temperatures:', uniqueTemp.length, uniqueTemp)
    console.log('Unique powers:', uniquePowerValues.length, uniquePowerValues)
    console.log('Grid Z dimensions:', gridZ.length, 'x', gridZ[0]?.length)
    console.log('Null values in grid:', gridZ.flat().filter(v => v === null).length, '/', gridZ.flat().length)

    // Debug: Show grid data structure
    console.log('=== Grid Data Sample ===')
    gridData.forEach((g, idx) => {
      console.log(`Temp ${g.temp}°C: ${g.points.length} points, Power range: ${g.points[0]?.power.toFixed(2)} - ${g.points[g.points.length-1]?.power.toFixed(2)}`)
    })

    // Debug: Show first row of grid
    console.log('=== Grid Row 0 (First Temperature) ===')
    console.log('Temperature:', uniqueTemp[0])
    console.log('Power values:', uniquePowerValues.slice(0, 10))
    console.log('Energy values:', gridZ[0]?.slice(0, 10))

    // Debug: Show raw data for comparison
    console.log('=== Raw Data at T=' + uniqueTemp[0] + ' ===')
    const temp0Data = gridData[0].points
    console.log('First 5 raw points:', temp0Data.slice(0, 5).map(p =>
      p && p.power !== undefined && p.energy !== undefined
        ? `P=${p.power.toFixed(2)}, E=${p.energy.toFixed(2)}`
        : 'undefined'
    ))

    // === PREPARE SLICE LINES ===
    // Power slice at T ≈ 25°C
    const powerSliceData = []
    for (let i = 0; i < data.X.length; i++) {
      if (Math.abs(data.Y[i] - 25) <= 2) {
        powerSliceData.push({ power: data.X[i], z: data.Z[i] })
      }
    }
    powerSliceData.sort((a, b) => a.power - b.power)

    // Average duplicates
    const powerMap = new Map()
    powerSliceData.forEach(d => {
      const powerKey = d.power.toFixed(3)
      if (powerMap.has(powerKey)) {
        const existing = powerMap.get(powerKey)
        powerMap.set(powerKey, (existing + d.z) / 2)
      } else {
        powerMap.set(powerKey, d.z)
      }
    })

    const powerSlice = Array.from(powerMap.entries())
      .map(([powerStr, z]) => ({ x: parseFloat(powerStr), y: 25, z }))
      .sort((a, b) => a.x - b.x)

    // Temperature slice at P ≈ P_C10 (or X0)
    const tempSliceData = []
    for (let i = 0; i < data.X.length; i++) {
      const powerDiff = Math.abs(data.X[i] - data.X0) / Math.max(data.X0, 0.001)
      if (powerDiff <= 0.2) {
        tempSliceData.push({ temp: data.Y[i], z: data.Z[i] })
      }
    }
    tempSliceData.sort((a, b) => a.temp - b.temp)

    // Average duplicates
    const tempMap = new Map()
    tempSliceData.forEach(d => {
      const tempKey = Math.round(d.temp * 10) / 10
      if (tempMap.has(tempKey)) {
        const existing = tempMap.get(tempKey)
        tempMap.set(tempKey, (existing + d.z) / 2)
      } else {
        tempMap.set(tempKey, d.z)
      }
    })

    const tempSlice = Array.from(tempMap.entries())
      .map(([temp, z]) => ({ x: data.X0, y: temp, z }))
      .sort((a, b) => a.y - b.y)

    // === CREATE TEMPERATURE SLICES FOR ALL 4 POWER CONDITIONS ===
    // === MATLAB-STYLE: Create ScatteredInterpolant from raw data ===
    const scatteredPoints = []
    const scatteredValues = []
    for (let i = 0; i < data.X.length; i++) {
      scatteredPoints.push({ x: data.X[i], y: data.Y[i] })
      scatteredValues.push(data.Z[i])
    }

    // Create interpolation function
    const interpolant = createScatteredInterpolant(scatteredPoints, scatteredValues)

    // === SLICE AT Y0 (T = 25°C): Use actual data points near T=25°C ===
    // Find data points where Y ≈ Y0 (within tolerance)
    const uniqueX = [...new Set(data.X)].sort((a, b) => a - b)
    const y0 = data.Y0 || 25

    // Create slice using actual data points at Y≈25°C (not interpolated)
    const createSliceAtY = (targetY, tolerance = 2) => {
      const sliceData = []
      for (let i = 0; i < data.Y.length; i++) {
        if (Math.abs(data.Y[i] - targetY) <= tolerance) {
          sliceData.push({ x: data.X[i], y: targetY, z: data.Z[i] })
        }
      }

      // Sort by X
      sliceData.sort((a, b) => a.x - b.x)

      // Remove duplicates - keep average Z for same X
      const xMap = new Map()
      sliceData.forEach(d => {
        const xKey = Math.round(d.x * 100) / 100  // Round to 2 decimals
        if (xMap.has(xKey)) {
          const existing = xMap.get(xKey)
          xMap.set(xKey, { x: xKey, y: targetY, z: (existing.z + d.z) / 2 })
        } else {
          xMap.set(xKey, { x: xKey, y: targetY, z: d.z })
        }
      })

      return Array.from(xMap.values()).sort((a, b) => a.x - b.x)
    }

    const sliceAtY0 = createSliceAtY(y0)

    // Get unique Y values for later use
    const uniqueY = [...new Set(data.Y.map(y => Math.round(y * 10) / 10))].sort((a, b) => a - b)

    console.log('=== Slice at Y0 (T=25°C) ===')
    console.log(`Slice at Y=${y0}°C: ${sliceAtY0.length} points`)

    // === MATLAB-STYLE: Create Delaunay triangulation for mesh3d ===
    const triangles = delaunay2D(scatteredPoints)

    // Flatten triangles into i, j, k arrays for Plotly mesh3d
    const meshI = [], meshJ = [], meshK = []
    triangles.forEach(tri => {
      meshI.push(tri.a)
      meshJ.push(tri.b)
      meshK.push(tri.c)
    })

    console.log('=== Delaunay Triangulation ===')
    console.log(`Points: ${scatteredPoints.length}, Triangles: ${triangles.length}`)

    // === CREATE WIREFRAME EDGES FROM TRIANGLES (like MATLAB's trisurf grid lines) ===
    // Extract unique edges from triangles to show the grid structure
    const edgeSet = new Set()
    const wireframeX = []
    const wireframeY = []
    const wireframeZ = []

    triangles.forEach(tri => {
      // Create edge keys (sorted to avoid duplicates)
      const edges = [
        [Math.min(tri.a, tri.b), Math.max(tri.a, tri.b)],
        [Math.min(tri.b, tri.c), Math.max(tri.b, tri.c)],
        [Math.min(tri.c, tri.a), Math.max(tri.c, tri.a)]
      ]

      edges.forEach(([i, j]) => {
        const key = `${i}-${j}`
        if (!edgeSet.has(key)) {
          edgeSet.add(key)
          // Add line segment with null separator for Plotly
          wireframeX.push(data.X[i], data.X[j], null)
          wireframeY.push(data.Y[i], data.Y[j], null)
          wireframeZ.push(data.Z[i], data.Z[j], null)
        }
      })
    })

    console.log(`Wireframe: ${edgeSet.size} unique edges`)

    // === CREATE SLICES BASED ON CATEGORY ===
    // Energy: T=25°C and W=C/10
    // Power: T=25°C and P=Standard (or I=Standard)
    // Resistance: T=25°C and SOC=80%

    // Use same slice logic as 2D plot - find data points near target X value
    const createSliceAtX = (targetX, tolerance = 0.3) => {
      if (!targetX || targetX === 0) return null

      const sliceData = []
      for (let i = 0; i < data.X.length; i++) {
        const xDiff = Math.abs(data.X[i] - targetX) / Math.max(Math.abs(targetX), 0.001)
        if (xDiff <= tolerance) {
          sliceData.push({ x: data.X[i], y: data.Y[i], z: data.Z[i] })
        }
      }

      // Sort by Y (temperature)
      sliceData.sort((a, b) => a.y - b.y)

      // Remove duplicates - keep average Z for same Y
      const yMap = new Map()
      sliceData.forEach(d => {
        const yKey = Math.round(d.y * 10) / 10
        if (yMap.has(yKey)) {
          const existing = yMap.get(yKey)
          yMap.set(yKey, { x: d.x, y: yKey, z: (existing.z + d.z) / 2 })
        } else {
          yMap.set(yKey, { x: d.x, y: yKey, z: d.z })
        }
      })

      return Array.from(yMap.values()).sort((a, b) => a.y - b.y)
    }

    // Create the appropriate X-slice based on category
    let xSlice = null
    let xSliceName = ''
    let xSliceValue = null

    if (categoryName === 'energy') {
      // Energy: W = C/10 or I = C/10 (depending on domain)
      const isEnergyDomain = data.xLabel === 'Power'
      xSliceValue = isEnergyDomain ? data.P_C10 : data.I_C10
      xSlice = createSliceAtX(xSliceValue)
      const sliceLabel = isEnergyDomain ? 'W' : 'I'
      xSliceName = `${sliceLabel} = C/10 (${xSliceValue?.toFixed(1)} ${data.xUnit})`
      console.log(`Energy slice: isEnergyDomain=${isEnergyDomain}, value=${xSliceValue?.toFixed(2)}, points=${xSlice?.length}`)
    } else if (categoryName === 'power') {
      // Power: P = Standard or I = Standard (depending on domain)
      const isPowerDomain = data.xLabel === 'Power'
      xSliceValue = isPowerDomain ? data.P_dc_25 : data.I_dc_25
      xSlice = createSliceAtX(xSliceValue)
      const sliceLabel = isPowerDomain ? 'P' : 'I'
      xSliceName = `${sliceLabel} = Standard (${xSliceValue?.toFixed(1)} ${data.xUnit})`
      console.log(`Power slice: isPowerDomain=${isPowerDomain}, value=${xSliceValue?.toFixed(2)}, points=${xSlice?.length}`)
    } else if (categoryName === 'dc-resistance') {
      // Resistance: SOC = 80%
      xSliceValue = 80
      xSlice = createSliceAtX(xSliceValue)
      xSliceName = `SOC = 80%`
    }

    console.log('=== Category-specific Slice ===')
    console.log(`Category: ${categoryName}, X-Slice: ${xSliceName}, ${xSlice?.length} points`)

    // === RENDER WITH PLOTLY (MATLAB-STYLE mesh3d) ===
    const traces = [
      // === MESH3D: Delaunay triangulated surface (like MATLAB's trisurf) ===
      {
        type: 'mesh3d',
        x: data.X,
        y: data.Y,
        z: data.Z,
        i: meshI,
        j: meshJ,
        k: meshK,
        intensity: data.Z,
        colorscale: categoryName === 'power'
          ? [
              [0, 'rgb(13, 124, 153)'],      // Cyan (low values)
              [0.3, 'rgb(45, 42, 38)'],      // Dark gray
              [0.6, 'rgb(234, 91, 12)'],     // Orange (medium)
              [1, 'rgb(255, 200, 100)']      // Light orange (high)
            ]
          : [
              [0, 'rgb(45, 42, 38)'],        // Dark gray (low)
              [0.5, 'rgb(234, 91, 12)'],     // Orange (medium)
              [1, 'rgb(255, 200, 100)']      // Light orange (high)
            ],
        opacity: 0.7,
        showscale: false,
        flatshading: false,  // Smooth shading like MATLAB
        lighting: {
          ambient: 0.6,
          diffuse: 0.8,
          specular: 0.3,
          roughness: 0.5,
          fresnel: 0.2
        },
        lightposition: {
          x: 5000,
          y: 5000,
          z: 5000
        },
        hovertemplate: `${data.xLabel}: %{x:.2f} ${data.xUnit}<br>${data.yLabel}: %{y:.1f} ${data.yUnit}<br>${data.zLabel}: %{z:.2f} ${data.zUnit}<extra></extra>`
      },
      // === WIREFRAME: Triangle edges (like MATLAB's trisurf grid lines) ===
      {
        type: 'scatter3d',
        mode: 'lines',
        name: 'Grid',
        x: wireframeX,
        y: wireframeY,
        z: wireframeZ,
        line: {
          color: 'rgba(234, 91, 12, 0.15)',  // Subtle orange wireframe
          width: 1
        },
        hoverinfo: 'skip',
        showlegend: false
      },
      // === SCATTER3D: Raw data points (like MATLAB's markers on trisurf) ===
      {
        type: 'scatter3d',
        mode: 'markers',
        name: 'Measured Points',
        x: data.X,
        y: data.Y,
        z: data.Z,
        marker: {
          size: 3,
          color: data.Z,
          colorscale: [
            [0, 'rgb(45, 42, 38)'],
            [0.5, 'rgb(234, 91, 12)'],
            [1, 'rgb(255, 200, 100)']
          ],
          opacity: 0.8
        },
        hovertemplate: `${data.xLabel}: %{x:.2f} ${data.xUnit}<br>${data.yLabel}: %{y:.1f} ${data.yUnit}<br>${data.zLabel}: %{z:.2f} ${data.zUnit}<extra></extra>`
      },
      // === SLICE at Y0 (T = 25°C) ===
      ...(sliceAtY0.length >= 2 ? [{
        type: 'scatter3d',
        mode: 'lines',
        name: `T = ${y0} ${data.yUnit}`,
        x: sliceAtY0.map(d => d.x),
        y: sliceAtY0.map(d => d.y),
        z: sliceAtY0.map(d => d.z),
        line: {
          color: '#ea5b0c',  // Orange
          width: 8
        },
        hovertemplate: `T = ${y0} ${data.yUnit}<br>${data.xLabel}: %{x:.2f} ${data.xUnit}<br>${data.zLabel}: %{z:.2f} ${data.zUnit}<extra></extra>`
      }] : []),
      // === SLICE at X0 (category-specific: W=C/10 for Energy, P=Standard for Power, SOC=80% for Resistance) ===
      ...(xSlice && xSlice.length >= 2 ? [{
        type: 'scatter3d',
        mode: 'lines',
        name: xSliceName,
        x: xSlice.map(d => d.x),
        y: xSlice.map(d => d.y),
        z: xSlice.map(d => d.z),
        line: {
          color: '#0d7c99',  // Cyan
          width: 8
        },
        hovertemplate: `${xSliceName}<br>${data.yLabel}: %{y:.1f} ${data.yUnit}<br>${data.zLabel}: %{z:.2f} ${data.zUnit}<extra></extra>`
      }] : [])
    ]

    const layout = {
      paper_bgcolor: 'transparent',
      plot_bgcolor: 'transparent',
      scene: {
        xaxis: {
          title: { text: `${data.xLabel} / ${data.xUnit}`, font: { color: '#cfd2d6', family: 'Roboto' } },
          gridcolor: 'rgba(234, 91, 12, 0.15)',  // Orange grid, dezenter
          zerolinecolor: 'rgba(255, 255, 255, 0.2)',
          tickfont: { color: '#999', family: 'Roboto' },
          backgroundcolor: 'rgba(0, 0, 0, 0)',
          automargin: true  // Verhindert Abschneiden
        },
        yaxis: {
          title: { text: `${data.yLabel} / ${data.yUnit}`, font: { color: '#cfd2d6', family: 'Roboto' } },
          gridcolor: 'rgba(234, 91, 12, 0.15)',  // Orange grid, dezenter
          zerolinecolor: 'rgba(255, 255, 255, 0.2)',
          tickfont: { color: '#999', family: 'Roboto' },
          backgroundcolor: 'rgba(0, 0, 0, 0)',
          automargin: true  // Verhindert Abschneiden
        },
        zaxis: {
          type: categoryName === 'power' ? 'log' : 'linear',
          title: { text: `${data.zLabel} / ${data.zUnit}`, font: { color: '#cfd2d6', family: 'Roboto' } },
          gridcolor: 'rgba(234, 91, 12, 0.15)',  // Orange grid, dezenter
          zerolinecolor: 'rgba(255, 255, 255, 0.2)',
          tickfont: { color: '#999', family: 'Roboto' },
          backgroundcolor: 'rgba(0, 0, 0, 0)',
          automargin: true  // Verhindert Abschneiden
        },
        bgcolor: 'rgba(0, 0, 0, 0)',
        camera: {
          eye: { x: 1.5, y: -1.5, z: 1.3 }  // Rotated 90° CW from default
        }
      },
      margin: { l: 0, r: 0, t: 0, b: 0 },
      autosize: true,  // Automatische Größenanpassung
      showlegend: true,  // Legende aktiviert
      legend: {
        x: 0.98,  // Innerhalb des Plots (98%)
        y: 0.98,  // Oben (98%)
        xanchor: 'right',  // Rechte Ecke des Plot-Bereichs
        yanchor: 'top',
        font: { color: '#cfd2d6', family: 'Roboto', size: 10 },
        bgcolor: 'rgba(16, 16, 16, 0.85)',
        bordercolor: 'rgba(234, 91, 12, 0.3)',
        borderwidth: 1
      }
    }

    const config = {
      responsive: true,
      displayModeBar: false,  // Toolbar komplett ausblenden
      displaylogo: false
    }

    console.log('=== Plotly Configuration ===')
    console.log('Traces:', traces.length, 'traces')
    console.log('Surface X (power):', uniquePowerValues.length, 'points', uniquePowerValues.slice(0, 5), '...')
    console.log('Surface Y (temp):', uniqueTemp.length, 'points', uniqueTemp)
    console.log('Surface Z (energy) dimensions:', gridZ.length, 'x', gridZ[0]?.length)
    console.log('Surface Z first row sample:', gridZ[0]?.slice(0, 5))
    console.log('Surface Z last row sample:', gridZ[gridZ.length-1]?.slice(0, 5))
    console.log('Power slice points:', powerSlice.length)
    console.log('Temp slice points:', tempSlice.length)

    window.Plotly.newPlot(plot3dRef.current, traces, layout, config)
      .then(() => {
        console.log('✅ Plotly 3D plot rendered successfully')
      })
      .catch((error) => {
        console.error('❌ Plotly error:', error)
      })
  }

  // Render based on dimension
  return (
    <div className="dimension-view-container">
      {dimension === '1d' && (
        <div className="dimension-1d">
          {category === 'energy' ? (
            <>
              {/* Show Energy or Capacity based on domain */}
              {activeDomain === 'energy-power' ? (
                <div className="kpi-grid-4">
                  <div className="kpi-tile" style={`border-color: #ea5b0c`}>
                    <div className="kpi-value" style={`color: #ea5b0c`}>{data.W_C10 ? formatValue(data.W_C10, data.zUnit) : '—'}</div>
                    <div className="kpi-unit">{data.zUnit}</div>
                    <div className="kpi-label">C/10</div>
                  </div>
                  <div className="kpi-tile" style={`border-color: #0077b6`}>
                    <div className="kpi-value" style={`color: #0077b6`}>{data.E_dc_25 ? formatValue(data.E_dc_25, data.zUnit) : '—'}</div>
                    <div className="kpi-unit">{data.zUnit}</div>
                    <div className="kpi-label">Standard</div>
                  </div>
                  <div className="kpi-tile" style={`border-color: #d62828`}>
                    <div className="kpi-value" style={`color: #d62828`}>{data.E_cont ? formatValue(data.E_cont, data.zUnit) : '—'}</div>
                    <div className="kpi-unit">{data.zUnit}</div>
                    <div className="kpi-label">Continuous</div>
                  </div>
                  <div className="kpi-tile" style={`border-color: #48cae4`}>
                    <div className="kpi-value" style={`color: #48cae4`}>{data.E_peak ? formatValue(data.E_peak, data.zUnit) : '—'}</div>
                    <div className="kpi-unit">{data.zUnit}</div>
                    <div className="kpi-label">Peak</div>
                  </div>
                </div>
              ) : (
                <div className="kpi-grid-4">
                  <div className="kpi-tile" style={`border-color: #ea5b0c`}>
                    <div className="kpi-value" style={`color: #ea5b0c`}>{data.C_C10 ? formatValue(data.C_C10, 'Ah') : '—'}</div>
                    <div className="kpi-unit">Ah{basis.value === 'grav' ? '/kg' : basis.value === 'vol' ? '/L' : ''}</div>
                    <div className="kpi-label">C/10</div>
                  </div>
                  <div className="kpi-tile" style={`border-color: #0077b6`}>
                    <div className="kpi-value" style={`color: #0077b6`}>{data.C_dc_25 ? formatValue(data.C_dc_25, 'Ah') : '—'}</div>
                    <div className="kpi-unit">Ah{basis.value === 'grav' ? '/kg' : basis.value === 'vol' ? '/L' : ''}</div>
                    <div className="kpi-label">Standard</div>
                  </div>
                  <div className="kpi-tile" style={`border-color: #d62828`}>
                    <div className="kpi-value" style={`color: #d62828`}>{data.C_cont ? formatValue(data.C_cont, 'Ah') : '—'}</div>
                    <div className="kpi-unit">Ah{basis.value === 'grav' ? '/kg' : basis.value === 'vol' ? '/L' : ''}</div>
                    <div className="kpi-label">Continuous</div>
                  </div>
                  <div className="kpi-tile" style={`border-color: #48cae4`}>
                    <div className="kpi-value" style={`color: #48cae4`}>{data.C_peak ? formatValue(data.C_peak, 'Ah') : '—'}</div>
                    <div className="kpi-unit">Ah{basis.value === 'grav' ? '/kg' : basis.value === 'vol' ? '/L' : ''}</div>
                    <div className="kpi-label">Peak</div>
                  </div>
                </div>
              )}
            </>
          ) : category === 'power' ? (
            <>
              {/* Show Power or Current based on domain */}
              {activeDomain === 'energy-power' ? (
                <div className="kpi-grid-4">
                  <div className="kpi-tile" style={`border-color: #ea5b0c`}>
                    <div className="kpi-value" style={`color: #ea5b0c`}>
                      {data.P_C10
                        ? data.P_C10 >= 1000
                          ? formatValue(data.P_C10 / 1000, `k${data.xUnit}`)
                          : formatValue(data.P_C10, data.xUnit)
                        : '—'}
                    </div>
                    <div className="kpi-unit">
                      {data.P_C10 >= 1000 ? `k${data.xUnit}` : data.xUnit}
                    </div>
                    <div className="kpi-label">C/10</div>
                  </div>
                  <div className="kpi-tile" style={`border-color: #0077b6`}>
                    <div className="kpi-value" style={`color: #0077b6`}>
                      {data.P_dc_25
                        ? data.P_dc_25 >= 1000
                          ? formatValue(data.P_dc_25 / 1000, `k${data.xUnit}`)
                          : formatValue(data.P_dc_25, data.xUnit)
                        : '—'}
                    </div>
                    <div className="kpi-unit">
                      {data.P_dc_25 >= 1000 ? `k${data.xUnit}` : data.xUnit}
                    </div>
                    <div className="kpi-label">Standard</div>
                  </div>
                  <div className="kpi-tile" style={`border-color: #d62828`}>
                    <div className="kpi-value" style={`color: #d62828`}>
                      {data.P_cont
                        ? data.P_cont >= 1000
                          ? formatValue(data.P_cont / 1000, `k${data.xUnit}`)
                          : formatValue(data.P_cont, data.xUnit)
                        : '—'}
                    </div>
                    <div className="kpi-unit">
                      {data.P_cont >= 1000 ? `k${data.xUnit}` : data.xUnit}
                    </div>
                    <div className="kpi-label">Continuous</div>
                  </div>
                  <div className="kpi-tile" style={`border-color: #48cae4`}>
                    <div className="kpi-value" style={`color: #48cae4`}>
                      {data.P_peak
                        ? data.P_peak >= 1000
                          ? formatValue(data.P_peak / 1000, `k${data.xUnit}`)
                          : formatValue(data.P_peak, data.xUnit)
                        : '—'}
                    </div>
                    <div className="kpi-unit">
                      {data.P_peak >= 1000 ? `k${data.xUnit}` : data.xUnit}
                    </div>
                    <div className="kpi-label">Peak</div>
                  </div>
                </div>
              ) : (
                <div className="kpi-grid-4">
                  <div className="kpi-tile" style={`border-color: #ea5b0c`}>
                    <div className="kpi-value" style={`color: #ea5b0c`}>
                      {data.I_C10
                        ? data.I_C10 >= 1000
                          ? formatValue(data.I_C10 / 1000, basis.value === 'grav' ? 'kA/kg' : basis.value === 'vol' ? 'kA/L' : 'kA')
                          : formatValue(data.I_C10, basis.value === 'grav' ? 'A/kg' : basis.value === 'vol' ? 'A/L' : 'A')
                        : '—'}
                    </div>
                    <div className="kpi-unit">
                      {data.I_C10 >= 1000
                        ? (basis.value === 'grav' ? 'kA/kg' : basis.value === 'vol' ? 'kA/L' : 'kA')
                        : (basis.value === 'grav' ? 'A/kg' : basis.value === 'vol' ? 'A/L' : 'A')}
                    </div>
                    <div className="kpi-label">C/10</div>
                  </div>
                  <div className="kpi-tile" style={`border-color: #0077b6`}>
                    <div className="kpi-value" style={`color: #0077b6`}>
                      {data.I_dc_25
                        ? data.I_dc_25 >= 1000
                          ? formatValue(data.I_dc_25 / 1000, basis.value === 'grav' ? 'kA/kg' : basis.value === 'vol' ? 'kA/L' : 'kA')
                          : formatValue(data.I_dc_25, basis.value === 'grav' ? 'A/kg' : basis.value === 'vol' ? 'A/L' : 'A')
                        : '—'}
                    </div>
                    <div className="kpi-unit">
                      {data.I_dc_25 >= 1000
                        ? (basis.value === 'grav' ? 'kA/kg' : basis.value === 'vol' ? 'kA/L' : 'kA')
                        : (basis.value === 'grav' ? 'A/kg' : basis.value === 'vol' ? 'A/L' : 'A')}
                    </div>
                    <div className="kpi-label">Standard</div>
                  </div>
                  <div className="kpi-tile" style={`border-color: #d62828`}>
                    <div className="kpi-value" style={`color: #d62828`}>
                      {data.I_cont
                        ? data.I_cont >= 1000
                          ? formatValue(data.I_cont / 1000, basis.value === 'grav' ? 'kA/kg' : basis.value === 'vol' ? 'kA/L' : 'kA')
                          : formatValue(data.I_cont, basis.value === 'grav' ? 'A/kg' : basis.value === 'vol' ? 'A/L' : 'A')
                        : '—'}
                    </div>
                    <div className="kpi-unit">
                      {data.I_cont >= 1000
                        ? (basis.value === 'grav' ? 'kA/kg' : basis.value === 'vol' ? 'kA/L' : 'kA')
                        : (basis.value === 'grav' ? 'A/kg' : basis.value === 'vol' ? 'A/L' : 'A')}
                    </div>
                    <div className="kpi-label">Continuous</div>
                  </div>
                  <div className="kpi-tile" style={`border-color: #48cae4`}>
                    <div className="kpi-value" style={`color: #48cae4`}>
                      {data.I_peak
                        ? data.I_peak >= 1000
                          ? formatValue(data.I_peak / 1000, basis.value === 'grav' ? 'kA/kg' : basis.value === 'vol' ? 'kA/L' : 'kA')
                          : formatValue(data.I_peak, basis.value === 'grav' ? 'A/kg' : basis.value === 'vol' ? 'A/L' : 'A')
                        : '—'}
                    </div>
                    <div className="kpi-unit">
                      {data.I_peak >= 1000
                        ? (basis.value === 'grav' ? 'kA/kg' : basis.value === 'vol' ? 'kA/L' : 'kA')
                        : (basis.value === 'grav' ? 'A/kg' : basis.value === 'vol' ? 'A/L' : 'A')}
                    </div>
                    <div className="kpi-label">Peak</div>
                  </div>
                </div>
              )}
            </>
          ) : category === 'voltage' ? (
            <div className="kpi-grid-4">
              <div className="kpi-tile" style={`border-color: #ea5b0c`}>
                <div className="kpi-value" style={`color: #ea5b0c`}>{data.V_nom ? formatValue(data.V_nom, 'V') : '—'}</div>
                <div className="kpi-unit">V</div>
                <div className="kpi-label">Nominal</div>
              </div>
              <div className="kpi-tile" style={`border-color: #0077b6`}>
                <div className="kpi-value" style={`color: #0077b6`}>{data.V_mean ? formatValue(data.V_mean, 'V') : '—'}</div>
                <div className="kpi-unit">V</div>
                <div className="kpi-label">Mean</div>
              </div>
              <div className="kpi-tile" style={`border-color: #d62828`}>
                <div className="kpi-value" style={`color: #d62828`}>{data.V_min ? formatValue(data.V_min, 'V') : '—'}</div>
                <div className="kpi-unit">V</div>
                <div className="kpi-label">Minimum</div>
              </div>
              <div className="kpi-tile" style={`border-color: #48cae4`}>
                <div className="kpi-value" style={`color: #48cae4`}>{data.V_max ? formatValue(data.V_max, 'V') : '—'}</div>
                <div className="kpi-unit">V</div>
                <div className="kpi-label">Maximum</div>
              </div>
            </div>
          ) : category === 'ac-impedance' ? (
            <div className="kpi-row">
              <div className="kpi-large">
                <div className="kpi-value">{data.R_ac_25 ? formatValue(data.R_ac_25, 'mΩ') : '—'}</div>
                <div className="kpi-unit">mΩ</div>
                <div className="kpi-label">R<sub>ac</sub> (25°C, SOC 80%)</div>
              </div>
              <div className="kpi-large">
                <div className="kpi-value">{data.R_pol_25 ? formatValue(data.R_pol_25, 'mΩ') : '—'}</div>
                <div className="kpi-unit">mΩ</div>
                <div className="kpi-label">R<sub>pol</sub> (25°C, SOC 80%)</div>
              </div>
            </div>
          ) : category === 'dc-resistance' ? (
            <div className="kpi-row single-column">
              <div className="kpi-large">
                <div className="kpi-value">{data.R_dc_25 ? formatValue(data.R_dc_25, 'mΩ') : '—'}</div>
                <div className="kpi-unit">mΩ</div>
                <div className="kpi-label">DCIR (25°C, SOC 80%)</div>
              </div>
            </div>
          ) : (
            <div className="kpi-large">
              <div className="kpi-value">{data.Z0 ? formatValue(data.Z0, data.zUnit) : '—'}</div>
              <div className="kpi-unit">{data.zUnit}</div>
              <div className="kpi-label">{data.zLabel} at {data.xLabel}={data.X0 ? formatValue(data.X0, data.xUnit) : '—'} {data.xUnit}, {data.yLabel}={data.Y0} {data.yUnit}</div>
            </div>
          )}
        </div>
      )}

      {(dimension === '2d' || (dimension === '3d' && category === 'ac-impedance')) && (
        <div className="dimension-2d">
          <div className={`plot-grid-2d ${
            category === 'voltage' || (category === 'ac-impedance' && dimension === '2d')
              ? 'single-plot'
              : category === 'ac-impedance' && dimension === '3d'
              ? 'bode-plots'
              : ''
          }`}>
            <div className="plot-card">
              <div ref={plot2dRef1} style="width: 100%; height: 100%;"></div>
            </div>
            <div className="plot-card">
              <div ref={plot2dRef2} style="width: 100%; height: 100%;"></div>
            </div>
          </div>
        </div>
      )}

      {dimension === '3d' && category !== 'ac-impedance' && (
        <div className="dimension-3d">
          <div className="plot-3d-container">
            <div ref={plot3dRef} style="width: 100%; height: 100%;"></div>
          </div>
        </div>
      )}
    </div>
  )
}

// Get data configuration for each category
function getDataForCategory(category, cellData, basisValue, domainValue, impedanceModeValue) {
  const m_kg = cellData?.mechanical?.mass_g ? cellData.mechanical.mass_g / 1000 : null
  const vol_L = cellData?.mechanical?.vol_cell_cm3 ? cellData.mechanical.vol_cell_cm3 / 1000 : null

  let X, Y, Z, X0, Y0, Z0, xLabel, yLabel, zLabel, xUnit, yUnit, zUnit

  if (category === 'energy') {
    // Energy/Capacity vs Power/Current vs Temperature (domain-switchable)
    // Raw data arrays (already normalized by dataService)
    const W_DCC = cellData?.electrical?.W_DCC || []  // Already in Wh
    const C_DCC = cellData?.electrical?.C_DCC || []  // Already in Ah
    const P_DCC = cellData?.electrical?.P_DCC || []  // Already in W
    const I_DCC = cellData?.electrical?.I_DCC || []  // Already in A
    const T_amb_DCC = cellData?.electrical?.T_amb_DCC || []  // Already in °C

    console.log('=== Energy Category Data ===')
    console.log('W_DCC length:', W_DCC.length)
    console.log('C_DCC length:', C_DCC.length)
    console.log('P_DCC length:', P_DCC.length)
    console.log('I_DCC length:', I_DCC.length)
    console.log('T_amb_DCC length:', T_amb_DCC.length)

    // 1D values: All 4 experimental conditions (C/10, Standard, Continuous, Peak)
    let W_C10, C_C10, P_C10, I_C10
    let E_dc_25, C_dc_25, P_dc_25, I_dc_25  // Standard
    let E_cont, C_cont, P_cont, I_cont        // Continuous
    let E_peak, C_peak, P_peak, I_peak        // Peak

    if (basisValue === 'grav') {
      // Gravimetric (per kg)
      // Get absolute values first, then divide by mass
      const E_c10_abs = cellData?.electrical?.E_c10_Wh || 0
      const W_dc_25_abs = cellData?.electrical?.W_dc_25_Wh || 0
      const W_cont_abs = cellData?.electrical?.W_cont_Wh || 0
      const W_peak_abs = cellData?.electrical?.W_peak_Wh || 0
      const C_c10_abs = cellData?.electrical?.C_c10_Ah || 0
      const C_cont_abs = cellData?.electrical?.C_cont_Ah || 0
      const C_peak_abs = cellData?.electrical?.C_peak_Ah || 0

      // Use API gravimetric values if available, otherwise calculate from absolute
      W_C10 = cellData?.electrical?.E_grav_Wh_per_kg || (m_kg ? E_c10_abs / m_kg : 0)
      C_C10 = cellData?.electrical?.C_grav_Ah_per_kg || (m_kg ? C_c10_abs / m_kg : 0)
      // NEW API FIELDS - Use P_dc_C10, I_dc_C10
      const P_dc_C10_abs = cellData?.electrical?.P_dc_C10 || 0
      const I_dc_C10_abs = cellData?.electrical?.I_dc_C10 || 0
      P_C10 = m_kg ? P_dc_C10_abs / m_kg : 0
      I_C10 = m_kg ? I_dc_C10_abs / m_kg : 0

      // Standard (25°C) - calculate from absolute - NEW API FIELDS
      E_dc_25 = m_kg ? W_dc_25_abs / m_kg : 0
      const C_dc_25_abs = cellData?.electrical?.C_dc_25_Ah || 0
      C_dc_25 = m_kg ? C_dc_25_abs / m_kg : 0  // Use calculated Standard capacity
      const P_dc_25_abs = cellData?.electrical?.P_dc_25_W || 0  // FIXED: Use P_dc_25_W with suffix
      const I_dc_25_abs = cellData?.electrical?.I_dc_25_A || 0  // FIXED: Use I_dc_25_A with suffix
      P_dc_25 = m_kg ? P_dc_25_abs / m_kg : 0
      I_dc_25 = m_kg ? I_dc_25_abs / m_kg : 0

      // Continuous - calculate from absolute - NEW API FIELDS
      const P_dc_cont_abs = cellData?.electrical?.P_dc_cont || 0
      const I_dc_cont_abs = cellData?.electrical?.I_dc_cont || 0
      E_cont = m_kg ? W_cont_abs / m_kg : 0
      C_cont = m_kg ? C_cont_abs / m_kg : 0
      P_cont = m_kg ? P_dc_cont_abs / m_kg : 0
      I_cont = m_kg ? I_dc_cont_abs / m_kg : 0

      // Peak - calculate from absolute - NEW API FIELDS
      E_peak = m_kg ? W_peak_abs / m_kg : 0
      C_peak = m_kg ? C_peak_abs / m_kg : 0
      const P_dc_peak_abs = cellData?.electrical?.P_dc_peak || 0
      const I_dc_peak_abs = cellData?.electrical?.I_dc_peak || 0
      P_peak = m_kg ? P_dc_peak_abs / m_kg : 0
      I_peak = m_kg ? I_dc_peak_abs / m_kg : 0
    } else if (basisValue === 'vol') {
      // Volumetric (per L)
      // Get absolute values first, then divide by volume
      const E_c10_abs = cellData?.electrical?.E_c10_Wh || 0
      const W_dc_25_abs = cellData?.electrical?.W_dc_25_Wh || 0
      const W_cont_abs = cellData?.electrical?.W_cont_Wh || 0
      const W_peak_abs = cellData?.electrical?.W_peak_Wh || 0
      const C_c10_abs = cellData?.electrical?.C_c10_Ah || 0
      const C_cont_abs = cellData?.electrical?.C_cont_Ah || 0
      const C_peak_abs = cellData?.electrical?.C_peak_Ah || 0

      // Use API volumetric values if available, otherwise calculate from absolute
      W_C10 = cellData?.electrical?.E_vol_Wh_per_L || (vol_L ? E_c10_abs / vol_L : 0)
      C_C10 = cellData?.electrical?.C_vol_Ah_per_L || (vol_L ? C_c10_abs / vol_L : 0)
      // NEW API FIELDS - Use P_dc_C10, I_dc_C10
      const P_dc_C10_abs = cellData?.electrical?.P_dc_C10 || 0
      const I_dc_C10_abs = cellData?.electrical?.I_dc_C10 || 0
      P_C10 = vol_L ? P_dc_C10_abs / vol_L : 0
      I_C10 = vol_L ? I_dc_C10_abs / vol_L : 0

      // Standard (25°C) - calculate from absolute - NEW API FIELDS
      E_dc_25 = vol_L ? W_dc_25_abs / vol_L : 0
      const C_dc_25_abs_vol = cellData?.electrical?.C_dc_25_Ah || 0
      C_dc_25 = vol_L ? C_dc_25_abs_vol / vol_L : 0  // Use calculated Standard capacity
      const P_dc_25_abs = cellData?.electrical?.P_dc_25_W || 0  // FIXED: Use P_dc_25_W with suffix
      const I_dc_25_abs = cellData?.electrical?.I_dc_25_A || 0  // FIXED: Use I_dc_25_A with suffix
      P_dc_25 = vol_L ? P_dc_25_abs / vol_L : 0
      I_dc_25 = vol_L ? I_dc_25_abs / vol_L : 0

      // Continuous - calculate from absolute - NEW API FIELDS
      const P_dc_cont_abs = cellData?.electrical?.P_dc_cont || 0
      const I_dc_cont_abs = cellData?.electrical?.I_dc_cont || 0
      E_cont = vol_L ? W_cont_abs / vol_L : 0
      C_cont = vol_L ? C_cont_abs / vol_L : 0
      P_cont = vol_L ? P_dc_cont_abs / vol_L : 0
      I_cont = vol_L ? I_dc_cont_abs / vol_L : 0

      // Peak - calculate from absolute - NEW API FIELDS
      E_peak = vol_L ? W_peak_abs / vol_L : 0
      C_peak = vol_L ? C_peak_abs / vol_L : 0
      const P_dc_peak_abs = cellData?.electrical?.P_dc_peak || 0
      const I_dc_peak_abs = cellData?.electrical?.I_dc_peak || 0
      P_peak = vol_L ? P_dc_peak_abs / vol_L : 0
      I_peak = vol_L ? I_dc_peak_abs / vol_L : 0
    } else {
      // Absolute - NEW API FIELDS
      W_C10 = cellData?.electrical?.E_c10_Wh || (cellData?.electrical?.W_C10 ? cellData.electrical.W_C10 / 3600 : 0)
      C_C10 = cellData?.electrical?.C_c10_Ah || (cellData?.electrical?.C_C10 ? cellData.electrical.C_C10 / 3600 : 0)
      P_C10 = cellData?.electrical?.P_dc_C10 || 0  // NEW API FIELD
      I_C10 = cellData?.electrical?.I_dc_C10 || 0  // NEW API FIELD

      // Standard (25°C) - use W_dc_25_Wh for energy, C_dc_25_Ah for capacity
      E_dc_25 = cellData?.electrical?.W_dc_25_Wh || 0
      C_dc_25 = cellData?.electrical?.C_dc_25_Ah || 0  // Use calculated Standard capacity
      P_dc_25 = cellData?.electrical?.P_dc_25_W || 0  // FIXED: Use P_dc_25_W with suffix
      I_dc_25 = cellData?.electrical?.I_dc_25_A || 0  // FIXED: Use I_dc_25_A with suffix

      // Continuous - NEW API FIELDS
      E_cont = cellData?.electrical?.W_cont_Wh || 0
      C_cont = cellData?.electrical?.C_cont_Ah || 0
      P_cont = cellData?.electrical?.P_dc_cont || 0  // NEW API FIELD
      I_cont = cellData?.electrical?.I_dc_cont || 0  // NEW API FIELD

      // Peak - NEW API FIELDS
      E_peak = cellData?.electrical?.W_peak_Wh || 0
      C_peak = cellData?.electrical?.C_peak_Ah || 0
      P_peak = cellData?.electrical?.P_dc_peak || 0  // NEW API FIELD
      I_peak = cellData?.electrical?.I_dc_peak || 0  // NEW API FIELD
    }

    console.log('Domain:', domainValue)
    console.log('W_C10:', W_C10, 'C_C10:', C_C10)
    console.log('P_C10:', P_C10, 'I_C10:', I_C10)

    // Choose arrays based on domain (default to energy-power if not set)
    const useEnergy = !domainValue || domainValue === 'energy-power'  // true = Energy+Power, false = Capacity+Current

    X = useEnergy ? [...P_DCC] : [...I_DCC]  // W or A
    Y = [...T_amb_DCC]  // °C
    Z = useEnergy ? [...W_DCC] : [...C_DCC]  // Wh or Ah

    // 1D reference point (in absolute units first)
    X0 = useEnergy ? P_C10 : I_C10
    Y0 = 25  // °C
    Z0 = useEnergy ? W_C10 : C_C10

    xLabel = useEnergy ? 'Power' : 'Current'
    yLabel = 'Ambient Temperature'
    zLabel = useEnergy ? 'Energy' : 'Capacity'
    xUnit = useEnergy ? 'W' : 'A'
    yUnit = '°C'
    zUnit = useEnergy ? 'Wh' : 'Ah'

    // Apply basis scaling to arrays AND X0/Z0
    if (basisValue === 'grav' && m_kg) {
      X = X.map(x => x / m_kg)
      Z = Z.map(z => z / m_kg)
      X0 = X0 / m_kg
      Z0 = Z0 / m_kg
      xUnit += '/kg'
      zUnit += '/kg'
    } else if (basisValue === 'vol' && vol_L) {
      X = X.map(x => x / vol_L)
      Z = Z.map(z => z / vol_L)
      X0 = X0 / vol_L
      Z0 = Z0 / vol_L
      xUnit += '/L'
      zUnit += '/L'
    }

    // If X0 is 0 after scaling, use median value as fallback
    if (!X0 || X0 === 0) {
      const sortedX = [...X].sort((a, b) => a - b)
      X0 = sortedX[Math.floor(sortedX.length / 2)] || 1
      console.warn(`${useEnergy ? 'P_C10' : 'I_C10'} was 0, using median as fallback:`, X0)
    }

    // Return with all 4 experimental conditions for 1D display
    return {
      X, Y, Z, X0, Y0, Z0,
      xLabel, yLabel, zLabel, xUnit, yUnit, zUnit,
      // All 4 conditions
      W_C10, C_C10, P_C10, I_C10,
      E_dc_25, C_dc_25, P_dc_25, I_dc_25,
      E_cont, C_cont, P_cont, I_cont,
      E_peak, C_peak, P_peak, I_peak,
      // Alias for backwards compatibility with spec points
      E_C10: W_C10  // Energy = W_C10 (already in Wh)
    }
  } else if (category === 'power') {
    // Power: Max Duration vs Power/Current vs Temperature (domain-switchable)
    // Raw data arrays (already normalized by dataService)
    const t_max_DCC = cellData?.electrical?.t_max_DCC || []  // Already in seconds
    const P_DCC = cellData?.electrical?.P_DCC || []  // Already in W
    const I_DCC = cellData?.electrical?.I_DCC || []  // Already in A
    const T_amb_DCC = cellData?.electrical?.T_amb_DCC || []  // Already in °C

    console.log('=== Power Category Data ===')
    console.log('t_max_DCC length:', t_max_DCC.length)
    console.log('P_DCC length:', P_DCC.length)
    console.log('I_DCC length:', I_DCC.length)
    console.log('T_amb_DCC length:', T_amb_DCC.length)
    console.log('First 5 t_max:', t_max_DCC.slice(0, 5))
    console.log('First 5 P_DCC:', P_DCC.slice(0, 5))
    console.log('First 5 I_DCC:', I_DCC.slice(0, 5))
    console.log('First 5 T_amb:', T_amb_DCC.slice(0, 5))

    // 1D values: All 4 conditions (C/10, Standard, Continuous, Peak) - NEW API FIELDS
    let P_peak, P_cont, P_dc_25, P_C10, I_peak, I_cont, I_dc_25, I_C10
    let t_max_C10, t_max_dc_25, t_max_cont, t_max_peak  // NEW: Time duration values [s]
    if (basisValue === 'grav') {
      // NEW API FIELDS - Get absolute values for C/10
      const P_dc_C10_abs = cellData?.electrical?.P_dc_C10 || 0
      const I_dc_C10_abs = cellData?.electrical?.I_dc_C10 || 0
      const P_dc_25_abs = cellData?.electrical?.P_dc_25_W || 0  // FIXED: Use P_dc_25_W with suffix
      const P_dc_cont_abs = cellData?.electrical?.P_dc_cont || 0
      const I_dc_25_abs = cellData?.electrical?.I_dc_25_A || 0  // FIXED: Use I_dc_25_A with suffix
      const I_dc_cont_abs = cellData?.electrical?.I_dc_cont || 0
      const P_dc_peak_abs = cellData?.electrical?.P_dc_peak || 0
      const I_dc_peak_abs = cellData?.electrical?.I_dc_peak || 0

      P_peak = m_kg ? P_dc_peak_abs / m_kg : 0  // W/kg
      P_cont = m_kg ? P_dc_cont_abs / m_kg : 0  // W/kg
      P_dc_25 = m_kg ? P_dc_25_abs / m_kg : 0  // W/kg - Standard dc_25
      P_C10 = m_kg ? P_dc_C10_abs / m_kg : 0  // Calculate from absolute
      I_peak = m_kg ? I_dc_peak_abs / m_kg : 0  // A/kg
      I_cont = m_kg ? I_dc_cont_abs / m_kg : 0  // A/kg
      I_dc_25 = m_kg ? I_dc_25_abs / m_kg : 0  // A/kg - Standard dc_25
      I_C10 = m_kg ? I_dc_C10_abs / m_kg : 0  // Calculate from absolute

      // NEW: Time duration values (absolute, no basis scaling)
      t_max_C10 = cellData?.electrical?.t_max_dc_C10 || 0  // seconds
      t_max_dc_25 = cellData?.electrical?.t_max_dc_25 || 0  // Standard dc_25
      t_max_cont = cellData?.electrical?.t_max_dc_cont || 0  // seconds
      t_max_peak = cellData?.electrical?.t_max_dc_peak || 0  // seconds
    } else if (basisValue === 'vol') {
      // NEW API FIELDS - Get absolute values for C/10
      const P_dc_C10_abs = cellData?.electrical?.P_dc_C10 || 0
      const I_dc_C10_abs = cellData?.electrical?.I_dc_C10 || 0
      const P_dc_25_abs = cellData?.electrical?.P_dc_25_W || 0  // FIXED: Use P_dc_25_W with suffix
      const P_dc_cont_abs = cellData?.electrical?.P_dc_cont || 0
      const I_dc_25_abs = cellData?.electrical?.I_dc_25_A || 0  // FIXED: Use I_dc_25_A with suffix
      const I_dc_cont_abs = cellData?.electrical?.I_dc_cont || 0
      const P_dc_peak_abs = cellData?.electrical?.P_dc_peak || 0
      const I_dc_peak_abs = cellData?.electrical?.I_dc_peak || 0

      // Calculate volumetric values from absolute
      P_peak = vol_L ? P_dc_peak_abs / vol_L : 0  // W/L
      P_cont = vol_L ? P_dc_cont_abs / vol_L : 0  // W/L
      P_dc_25 = vol_L ? P_dc_25_abs / vol_L : 0  // W/L - Standard dc_25
      P_C10 = vol_L ? P_dc_C10_abs / vol_L : 0  // Calculate from absolute
      I_peak = vol_L ? I_dc_peak_abs / vol_L : 0  // A/L
      I_cont = vol_L ? I_dc_cont_abs / vol_L : 0  // A/L
      I_dc_25 = vol_L ? I_dc_25_abs / vol_L : 0  // A/L - Standard dc_25
      I_C10 = vol_L ? I_dc_C10_abs / vol_L : 0  // Calculate from absolute

      // NEW: Time duration values (absolute, no basis scaling)
      t_max_C10 = cellData?.electrical?.t_max_dc_C10 || 0  // seconds
      t_max_dc_25 = cellData?.electrical?.t_max_dc_25 || 0  // Standard dc_25
      t_max_cont = cellData?.electrical?.t_max_dc_cont || 0  // seconds
      t_max_peak = cellData?.electrical?.t_max_dc_peak || 0  // seconds
    } else {
      // Absolute - NEW API FIELDS
      P_peak = cellData?.electrical?.P_dc_peak || 0  // W
      P_cont = cellData?.electrical?.P_dc_cont || 0  // W
      P_dc_25 = cellData?.electrical?.P_dc_25_W || 0  // FIXED: Use P_dc_25_W with suffix
      P_C10 = cellData?.electrical?.P_dc_C10 || 0  // W
      I_peak = cellData?.electrical?.I_dc_peak || 0  // A
      I_cont = cellData?.electrical?.I_dc_cont || 0  // A
      I_dc_25 = cellData?.electrical?.I_dc_25_A || 0  // FIXED: Use I_dc_25_A with suffix
      I_C10 = cellData?.electrical?.I_dc_C10 || 0  // A

      // NEW: Time duration values (absolute)
      t_max_C10 = cellData?.electrical?.t_max_dc_C10 || 0  // seconds
      t_max_dc_25 = cellData?.electrical?.t_max_dc_25 || 0  // Standard dc_25
      t_max_cont = cellData?.electrical?.t_max_dc_cont || 0  // seconds
      t_max_peak = cellData?.electrical?.t_max_dc_peak || 0  // seconds
    }

    console.log('1D Power values - P_peak:', P_peak, 'P_cont:', P_cont, 'I_peak:', I_peak, 'I_cont:', I_cont, 'basis:', basisValue)

    console.log('P_peak:', P_peak, 'I_peak:', I_peak)
    console.log('P_cont:', P_cont, 'I_cont:', I_cont)
    console.log('P_dc_25:', P_dc_25, 'I_dc_25:', I_dc_25)
    console.log('Domain:', domainValue, 'usePower:', !domainValue || domainValue === 'energy-power')

    // Choose arrays based on domain (default to energy-power if not set)
    const usePower = !domainValue || domainValue === 'energy-power'  // true = Power, false = Current

    X = usePower ? [...P_DCC] : [...I_DCC]  // W or A
    Y = [...T_amb_DCC]  // °C
    Z = [...t_max_DCC]  // seconds

    // 1D reference point - get from basis-specific values (already scaled)
    X0 = usePower ? P_dc_25 : I_dc_25
    Y0 = 25  // °C

    console.log('=== Power Category X0 Reference Point ===')
    console.log('Using:', usePower ? 'P_dc_25' : 'I_dc_25', '=', X0, usePower ? 'W' : 'A', '(basis:', basisValue + ')')

    // Apply basis scaling to X array (X0 already has correct basis from above)
    if (basisValue === 'grav' && m_kg) {
      X = X.map(x => x / m_kg)
    } else if (basisValue === 'vol' && vol_L) {
      X = X.map(x => x / vol_L)
    }

    // If X0 is 0, use median as fallback
    if (!X0 || X0 === 0) {
      const sortedX = [...X].sort((a, b) => a - b)
      X0 = sortedX[Math.floor(sortedX.length / 2)] || 1
      console.warn(`${usePower ? 'P_dc_25' : 'I_dc_25'} was 0, using median as fallback:`, X0)
    }

    // Find Z0 by looking for closest match to X0 at 25°C (after X is scaled)
    let closestIdx = 0
    let minDist = Infinity
    for (let i = 0; i < X.length; i++) {
      const dist = Math.abs(X[i] - X0) + Math.abs(Y[i] - 25)
      if (dist < minDist) {
        minDist = dist
        closestIdx = i
      }
    }
    Z0 = Z[closestIdx] || 0

    xLabel = usePower ? 'Power' : 'Current'
    yLabel = 'Ambient Temperature'
    zLabel = 'Max Duration'

    if (basisValue === 'grav') {
      xUnit = usePower ? 'W/kg' : 'A/kg'
    } else if (basisValue === 'vol') {
      xUnit = usePower ? 'W/L' : 'A/L'
    } else {
      xUnit = usePower ? 'W' : 'A'
    }
    yUnit = '°C'
    zUnit = 's'

    console.log('Power Category Final Data:')
    console.log('X (power/current) length:', X.length, 'first 3:', X.slice(0, 3))
    console.log('Y (temp) length:', Y.length, 'first 3:', Y.slice(0, 3))
    console.log('Z (duration) length:', Z.length, 'first 3:', Z.slice(0, 3))
    console.log('X0:', X0, 'Y0:', Y0, 'Z0:', Z0)
    console.log('Labels:', xLabel, yLabel, zLabel)
    console.log('Units:', xUnit, yUnit, zUnit)

    // Return with all Power and Current values for 1D display (4 conditions) + NEW TIME DURATIONS
    return {
      X, Y, Z, X0, Y0, Z0,
      xLabel, yLabel, zLabel, xUnit, yUnit, zUnit,
      P_peak, P_cont, P_dc_25, P_C10, I_peak, I_cont, I_dc_25, I_C10,
      // NEW: Time duration values for spec points in power plots
      t_max_C10, t_max_dc_25, t_max_cont, t_max_peak
    }
  } else if (category === 'voltage') {
    // Voltage vs Charge (C/10 discharge curve)
    const DCC_C10_V = cellData?.electrical_DCC_C10_V || []  // Voltage [V]
    const DCC_C10_Q = cellData?.electrical_DCC_C10_Q || []  // Charge [Ah]

    console.log('=== Voltage Category Data ===')
    console.log('DCC_C10_V length:', DCC_C10_V.length, 'first 5:', DCC_C10_V.slice(0, 5))
    console.log('DCC_C10_Q length:', DCC_C10_Q.length, 'first 5:', DCC_C10_Q.slice(0, 5))
    console.log('V_nom:', cellData?.electrical?.V_nom, 'Q_nom:', cellData?.electrical?.Q_nom)

    // For voltage, we show V vs Q (2D only, no temperature dimension)
    X = [...DCC_C10_Q]  // Charge [Ah]
    Y = [...DCC_C10_V]  // Voltage [V] - using Y for voltage to create V vs Q plot
    Z = []  // No Z dimension for voltage

    // 1D values: 4 voltage characteristics
    const V_nom = cellData?.electrical?.V_nom || 3.7    // Nominal voltage [V]
    const V_mean = cellData?.electrical?.V_mean || 3.7  // Mean voltage [V]
    const V_max = cellData?.electrical?.V_max || 4.2    // Max voltage [V]
    const V_min = cellData?.electrical?.V_min || 2.5    // Min voltage [V]

    console.log('Voltage values - V_nom:', V_nom, 'V_mean:', V_mean, 'V_max:', V_max, 'V_min:', V_min)

    X0 = 0  // No reference point for charge
    Y0 = V_mean
    Z0 = 0

    xLabel = 'Charge'
    yLabel = 'Voltage'
    zLabel = ''
    xUnit = 'Ah'
    yUnit = 'V'
    zUnit = ''

    return {
      X, Y, Z, X0, Y0, Z0,
      xLabel, yLabel, zLabel, xUnit, yUnit, zUnit,
      V_nom, V_mean, V_max, V_min
    }
  } else if (category === 'ac-impedance') {
    // AC Impedance: EIS (Electrochemical Impedance Spectroscopy)
    // Nyquist plot: -Z_imag vs Z_real

    const EIS_25_f = cellData?.electrical?.EIS_25_f || []  // Frequency [Hz]
    const EIS_25_Z_real = cellData?.electrical?.EIS_25_Z_real || []  // Real impedance [mΩ]
    const EIS_25_Z_imag = cellData?.electrical?.EIS_25_Z_imag || []  // Imaginary impedance [mΩ]

    console.log('AC Impedance - EIS_25_f length:', EIS_25_f.length)
    console.log('AC Impedance - EIS_25_Z_real length:', EIS_25_Z_real.length)
    console.log('AC Impedance - EIS_25_Z_imag length:', EIS_25_Z_imag.length)

    // For AC mode, we only have 2D (Nyquist plot), no 3D
    X = [...EIS_25_Z_real]  // Real impedance [mΩ]
    Y = EIS_25_Z_imag.map(z => -z)  // Negative imaginary (inverted Y-axis) [mΩ]
    Z = []  // No Z dimension for AC

    // 1D values - include both AC and DC for comparison
    const R_ac_25 = cellData?.electrical?.R_ac_mOhm || 0
    const R_dc_25 = cellData?.electrical?.R_dc_mOhm || 0  // Also show DC for comparison
    const R_EIS_25 = cellData?.electrical?.R_EIS_mOhm || 0
    const R_pol_25 = cellData?.electrical?.R_pol_25 || 0

    X0 = R_ac_25
    Y0 = 0
    Z0 = 0

    xLabel = 'Zreal'
    yLabel = '-Zimag'
    zLabel = ''
    xUnit = 'mΩ'
    yUnit = 'mΩ'
    zUnit = ''

    return {
      X, Y, Z, X0, Y0, Z0,
      xLabel, yLabel, zLabel, xUnit, yUnit, zUnit,
      R_ac_25, R_dc_25, R_EIS_25, R_pol_25,
      EIS_25_f,
      mode: 'ac'
    }
  } else if (category === 'dc-resistance') {
    // DCIR
    // R_10_DCP vs SOC_DCP vs T_amb_DCP (3D surface)

    const R_10_DCP = cellData?.electrical?.R_10_DCP || []  // DCIR [mΩ]
    const SOC_DCP = cellData?.electrical?.SOC_DCP || []    // State of charge [0-1]
    const T_amb_DCP = cellData?.electrical?.T_amb_DCP || []  // Ambient temperature [°C]

    console.log('DCIR - R_10_DCP length:', R_10_DCP.length)
    console.log('DCIR - SOC_DCP length:', SOC_DCP.length)
    console.log('DCIR - T_amb_DCP length:', T_amb_DCP.length)

    // Convert SOC from fraction to percentage
    X = SOC_DCP.map(soc => soc * 100)  // SOC [%]
    Y = [...T_amb_DCP]  // Temperature [°C]
    Z = [...R_10_DCP]  // Resistance [mΩ]

    // 1D values: R_dc_25 and R_ac_25
    const R_dc_25 = cellData?.electrical?.R_dc_mOhm || 0  // DCIR at 25°C [mΩ]
    const R_ac_25 = cellData?.electrical?.R_ac_mOhm || 0  // AC resistance at 25°C [mΩ]

    console.log('R_dc_25:', R_dc_25, 'R_ac_25:', R_ac_25)

    // Reference point: SOC=80%, T=25°C
    X0 = 80  // % SOC
    Y0 = 25  // °C

    // Find Z0 by looking for closest match
    let closestIdx = 0
    let minDist = Infinity
    for (let i = 0; i < X.length; i++) {
      const dist = Math.abs(X[i] - 80) + Math.abs(Y[i] - 25)
      if (dist < minDist) {
        minDist = dist
        closestIdx = i
      }
    }
    Z0 = Z[closestIdx] || R_dc_25

    xLabel = 'SOC'
    yLabel = 'Ambient Temperature'
    zLabel = 'DCIR'
    xUnit = '%'
    yUnit = '°C'
    zUnit = 'mΩ'

    return {
      X, Y, Z, X0, Y0, Z0,
      xLabel, yLabel, zLabel, xUnit, yUnit, zUnit,
      R_dc_25, R_ac_25,
      mode: 'dc'
    }
  } else {
    // Fallback for unknown category
    X = []
    Y = []
    Z = []
    X0 = 0
    Y0 = 0
    Z0 = 0
    xLabel = ''
    yLabel = ''
    zLabel = ''
    xUnit = ''
    yUnit = ''
    zUnit = ''

    return { X, Y, Z, X0, Y0, Z0, xLabel, yLabel, zLabel, xUnit, yUnit, zUnit }
  }
}
